// src/stores/AdminStore.ts
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";
var initialState = {
  selectedOrganisation: null,
  selectedTwin: null
};
var buildStore = () => {
  const useStore = createWithEqualityFn()((set, get) => {
    const storeData = {
      ...initialState,
      setSelectedOrganisation: (selectedOrganisation) => {
        set({ selectedOrganisation });
      },
      updateSelectedOrganisation: (id, name, bId) => {
        set({
          selectedOrganisation: { id, name, bId }
        });
      },
      clearSelectedOrganisation: () => {
        set({ selectedOrganisation: null });
      },
      // Twin Actions
      setSelectedTwin: (selectedTwin) => {
        set({ selectedTwin });
      },
      updateSelectedTwin: (id, name, bId) => {
        set({
          selectedTwin: { id, name, bId }
        });
      },
      clearSelectedTwin: () => {
        set({ selectedTwin: null });
      },
      // Organisation Helper methods
      hasSelectedOrganisation: () => {
        return get().selectedOrganisation !== null;
      },
      getOrganisationId: () => {
        return get().selectedOrganisation?.id || null;
      },
      getOrganisationName: () => {
        return get().selectedOrganisation?.name || null;
      },
      getOrganisationBId: () => {
        return get().selectedOrganisation?.bId || null;
      },
      // Twin Helper methods
      hasSelectedTwin: () => {
        return get().selectedTwin !== null;
      },
      getTwinId: () => {
        return get().selectedTwin?.id || null;
      },
      getTwinName: () => {
        return get().selectedTwin?.name || null;
      },
      getTwinBId: () => {
        return get().selectedTwin?.bId || null;
      }
    };
    return storeData;
  }, shallow);
  return useStore;
};
var useAdminStore = buildStore();

// src/stores/FloorplanStore.ts
import { create as create3 } from "zustand";
import { devtools as devtools2 } from "zustand/middleware";
import {
  findLatestDesign,
  createEmptyFloor,
  ensurePlanItemIds,
  entityIdentityKey,
  pruneCompositesForRemovedRefs,
  pruneObserversForRemovedRefs,
  removeCameraFromLayout,
  foldLegacyBackground,
  cleanUnderlayName,
  underlayContrast,
  normaliseFloorLevels,
  normaliseFloorPlacement,
  cleanPlacement,
  isIdentityPlacement,
  placementOf,
  reexpressUnderlay,
  samePlacement,
  isLive,
  firstLiveSurfaces,
  pointInPolygon,
  polygonInteriorPoint,
  spatialParentOfItem,
  spatialParentOfSurface,
  usablePoly,
  asArray as asArray2,
  isEntityCapacity,
  buildTwinNameIndex,
  defaultFloorName,
  isNameTaken,
  nameForNewcomer,
  nextFreeName,
  buildFloorRemovalRecord,
  cascadeFloorCameras,
  floorRemovedRefs,
  newWallId,
  nextFloorId,
  restoreFloorIntoLayout,
  promoteAfterRemoval,
  recordCodeChange
} from "@repo/front-end-utils";

// src/stores/EditorStore.ts
import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
var PLAN_MODE_DESCRIPTION = {
  edit: "Add/change equipment, spaces, walls and cameras",
  align: "Manage plan underlays & floor alignment"
};
var EDITOR_MIN_ZOOM = 0.1;
var EDITOR_MAX_ZOOM = 5;
var MAX_UNDO_STACK = 50;
var useEditorStore = create()(
  devtools(
    subscribeWithSelector((set, get) => ({
      toolMode: "select",
      streamDragActive: false,
      wallRun: null,
      selectedEntityIds: [],
      selectedFloorId: null,
      modifiedEntityIds: /* @__PURE__ */ new Set(),
      originalPositions: /* @__PURE__ */ new Map(),
      zoom: 1,
      panOffset: { x: 0, y: 0 },
      viewRotation: 0,
      gridSnap: true,
      gridSize: 10,
      undoStack: [],
      redoStack: [],
      mousePosition: { x: 0, y: 0 },
      highlightedEntityId: null,
      flyRequest: null,
      planViewInsets: null,
      heldSelectionFloor: null,
      heldFloorShown: null,
      frameRequest: null,
      searchLitIds: null,
      jumped: null,
      cameraDeleteRequest: null,
      itemDeleteRequest: null,
      planMode: "edit",
      showMap: true,
      hiddenFloorIds: [],
      shownFloorIds: [],
      selectedUnderlayId: null,
      scalePromptUnderlayId: null,
      underlayPreview: null,
      floorPlacementPreview: null,
      deletePreview: null,
      decodedUnderlaySrcs: {},
      // Leaving the wall tool ends any run it had open (R2.6): its committed walls are already
      // in the plan, so only the run itself — and with it the preview — goes.
      setToolMode: (mode) => set((state) => ({
        toolMode: mode,
        ...mode !== "draw_wall" && state.wallRun ? { wallRun: null } : {}
      })),
      setWallRun: (run) => set({ wallRun: run }),
      setStreamDragActive: (active) => set({ streamDragActive: active }),
      selectEntity: (id, multi = false) => set((state) => ({
        // A held selection is on another floor (`plan-fly-to`, R4.7): a shift-click on the floor
        // shown starts afresh rather than joining it, so no selection ever spans two floors.
        selectedEntityIds: multi && state.heldSelectionFloor === null ? state.selectedEntityIds.includes(id) ? state.selectedEntityIds.filter((e) => e !== id) : [...state.selectedEntityIds, id] : [id],
        selectedFloorId: null,
        heldSelectionFloor: null
      })),
      clearSelection: () => set({ selectedEntityIds: [], selectedFloorId: null, heldSelectionFloor: null }),
      selectFloor: (id) => set(
        id === null ? { selectedFloorId: null } : { selectedFloorId: id, selectedEntityIds: [], heldSelectionFloor: null }
      ),
      markEntityModified: (id) => set((state) => {
        const next = new Set(state.modifiedEntityIds);
        next.add(id);
        return { modifiedEntityIds: next };
      }),
      storeOriginalPosition: (id, x, y) => set((state) => {
        if (state.originalPositions.has(id)) return {};
        const next = new Map(state.originalPositions);
        next.set(id, { x, y });
        return { originalPositions: next };
      }),
      clearModified: () => set({
        modifiedEntityIds: /* @__PURE__ */ new Set(),
        originalPositions: /* @__PURE__ */ new Map()
      }),
      setZoom: (zoom) => set({ zoom: Math.max(EDITOR_MIN_ZOOM, Math.min(EDITOR_MAX_ZOOM, zoom)) }),
      setPanOffset: (offset) => set({ panOffset: offset }),
      setViewRotation: (degrees) => set({ viewRotation: (degrees % 360 + 360) % 360 }),
      rotateViewCW: () => set((state) => ({ viewRotation: (state.viewRotation + 90) % 360 })),
      rotateViewCCW: () => set((state) => ({ viewRotation: (state.viewRotation + 270) % 360 })),
      toggleGridSnap: () => set((state) => ({ gridSnap: !state.gridSnap })),
      setGridSize: (size) => set({ gridSize: size }),
      setMousePosition: (pos) => set({ mousePosition: pos }),
      pushSnapshot: (layout, scope = "plan") => set((state) => ({
        undoStack: [
          ...state.undoStack.slice(-MAX_UNDO_STACK + 1),
          { layout: structuredClone(layout), timestamp: Date.now(), scope }
        ],
        redoStack: []
      })),
      topScope: (stack) => {
        const list = stack === "undo" ? get().undoStack : get().redoStack;
        return list.length > 0 ? list[list.length - 1].scope : null;
      },
      undo: (currentLayout) => {
        const { undoStack } = get();
        if (undoStack.length === 0) return void 0;
        const snapshot = undoStack[undoStack.length - 1];
        const currentSnapshot = {
          layout: structuredClone(currentLayout),
          timestamp: Date.now(),
          scope: snapshot.scope
        };
        set((state) => ({
          undoStack: state.undoStack.slice(0, -1),
          redoStack: [...state.redoStack, currentSnapshot],
          // A live PDF preview would hide the step just restored (`underlay-transform`, review).
          underlayPreview: null,
          floorPlacementPreview: null
        }));
        return snapshot;
      },
      redo: (currentLayout) => {
        const { redoStack } = get();
        if (redoStack.length === 0) return void 0;
        const snapshot = redoStack[redoStack.length - 1];
        const currentSnapshot = {
          layout: structuredClone(currentLayout),
          timestamp: Date.now(),
          scope: snapshot.scope
        };
        set((state) => ({
          redoStack: state.redoStack.slice(0, -1),
          undoStack: [...state.undoStack, currentSnapshot],
          // A live PDF preview would hide the step just restored (`underlay-transform`, review).
          underlayPreview: null,
          floorPlacementPreview: null
        }));
        return snapshot;
      },
      clearHistory: () => set({ undoStack: [], redoStack: [] }),
      highlightEntity: (id) => {
        set({ highlightedEntityId: id });
        setTimeout(() => {
          if (get().highlightedEntityId === id) {
            set({ highlightedEntityId: null });
          }
        }, 3e3);
      },
      requestFly: (id, opts) => set((state) => ({
        flyRequest: {
          id,
          onlyIfHidden: opts?.onlyIfHidden === true,
          n: (state.flyRequest?.n ?? 0) + 1
        }
      })),
      clearFlyRequest: () => set({ flyRequest: null }),
      setPlanViewInsets: (insets) => set({ planViewInsets: insets }),
      setHeldSelectionFloor: (floorIndex) => set({ heldSelectionFloor: floorIndex }),
      markHeldFloorShown: () => set((state) => ({ heldFloorShown: { n: (state.heldFloorShown?.n ?? 0) + 1 } })),
      frameEntities: (ids) => set({ frameRequest: { ids: [...ids] } }),
      clearFrameRequest: () => set({ frameRequest: null }),
      setSearchLit: (ids) => set((state) => {
        const current = state.searchLitIds;
        if (ids.length === 0) return current === null ? state : { searchLitIds: null };
        const next = new Set(ids);
        if (current && current.size === next.size && ids.every((id) => current.has(id))) {
          return state;
        }
        return { searchLitIds: next };
      }),
      markJumped: (id) => set((state) => ({ jumped: { id, n: (state.jumped?.n ?? 0) + 1 } })),
      requestCameraDelete: (refid) => set((state) => ({
        cameraDeleteRequest: { refid, nonce: (state.cameraDeleteRequest?.nonce ?? 0) + 1 }
      })),
      clearCameraDeleteRequest: () => set({ cameraDeleteRequest: null }),
      requestItemDelete: (target, opts) => {
        const refids = Array.isArray(target) ? target : [target];
        if (refids.length === 0) return;
        set((state) => ({
          itemDeleteRequest: {
            refid: refids[0],
            refids,
            ...opts?.floorIndex != null ? { floorIndex: opts.floorIndex } : {},
            nonce: (state.itemDeleteRequest?.nonce ?? 0) + 1
          }
        }));
      },
      clearItemDeleteRequest: () => set({ itemDeleteRequest: null }),
      setShowMap: (show) => set({ showMap: show }),
      setPlanMode: (mode) => set(
        (state) => mode === "align" ? {
          planMode: mode,
          selectedEntityIds: [],
          selectedFloorId: null,
          heldSelectionFloor: null,
          toolMode: "select",
          wallRun: null,
          underlayPreview: null,
          floorPlacementPreview: null
        } : {
          planMode: mode,
          selectedUnderlayId: null,
          underlayPreview: null,
          floorPlacementPreview: null,
          // The two reference tools are Build-floors tools (`underlay-layers`,
          // `underlay-transform`): leaving with one armed would leave Edit floor's clicks
          // captured by nothing.
          ...state.toolMode === "calibrate_scale" || state.toolMode === "scale_underlay" ? { toolMode: "select" } : {}
        }
      ),
      setFloorVisible: (floorId, visible, drawnByDefault = false) => set((state) => {
        const hidden = state.hiddenFloorIds.filter((id) => id !== floorId);
        const shown = state.shownFloorIds.filter((id) => id !== floorId);
        if (!visible) return { hiddenFloorIds: [...hidden, floorId], shownFloorIds: shown };
        return {
          hiddenFloorIds: hidden,
          shownFloorIds: drawnByDefault ? shown : [...shown, floorId]
        };
      }),
      setAllFloorsVisible: (visible, floorIds) => {
        const ids = [...new Set(floorIds)];
        set(
          visible ? { hiddenFloorIds: [], shownFloorIds: ids } : { hiddenFloorIds: ids, shownFloorIds: [] }
        );
      },
      forgetFloor: (floorId) => set(
        (state) => state.hiddenFloorIds.includes(floorId) || state.shownFloorIds.includes(floorId) ? {
          hiddenFloorIds: state.hiddenFloorIds.filter((id) => id !== floorId),
          shownFloorIds: state.shownFloorIds.filter((id) => id !== floorId)
        } : {}
      ),
      /*
       * NOT the scale-prompt request: a host may arm it in the same tick as it mounts the planner,
       * whose mount-time reset would otherwise wipe it before the prompt is ever seen (found by
       * review, when the wizard's former *start from a PDF* card did exactly that). A leftover request is harmless — the prompt shows only for an
       * existing, uncalibrated PDF, named by an id no other plan carries.
       */
      resetPlanView: () => set({
        planMode: "edit",
        showMap: true,
        hiddenFloorIds: [],
        shownFloorIds: [],
        selectedUnderlayId: null,
        underlayPreview: null,
        floorPlacementPreview: null
      }),
      /*
       * Choosing another PDF disarms an armed Set scale: its first point was measured on the PDF
       * selected before, and the scale it would set must never land on a different one (PR #1863
       * review). Arming selects first and arms second, so this never undoes an arm.
       */
      selectUnderlay: (underlayId) => set(
        (state) => state.toolMode === "calibrate_scale" && underlayId !== state.selectedUnderlayId ? { selectedUnderlayId: underlayId, toolMode: "select" } : { selectedUnderlayId: underlayId }
      ),
      setScalePromptUnderlay: (underlayId) => set({ scalePromptUnderlayId: underlayId }),
      setUnderlayPreview: (preview) => set({ underlayPreview: preview }),
      setFloorPlacementPreview: (preview) => set({ floorPlacementPreview: preview }),
      setDeletePreview: (preview) => set({ deletePreview: preview }),
      markUnderlayDecoded: (underlayId, src) => set(
        (s) => s.decodedUnderlaySrcs[underlayId] === src ? s : { decodedUnderlaySrcs: { ...s.decodedUnderlaySrcs, [underlayId]: src } }
      ),
      forgetUnderlayDecoded: (underlayId, src) => set((s) => {
        if (s.decodedUnderlaySrcs[underlayId] !== src) return s;
        const { [underlayId]: _gone, ...rest } = s.decodedUnderlaySrcs;
        return { decodedUnderlaySrcs: rest };
      })
    })),
    { name: "editor-store" }
  )
);

// src/stores/EquipmentCatalogStore.ts
import { create as create2 } from "zustand";
import {
  createTwinAsset,
  getTwinAssetsOrThrow,
  updateTwinAsset,
  MIDDLEWARE_REQUEST_TIMEOUT_MS,
  OFFLINE_MESSAGE,
  REPLY_UNREADABLE
} from "@repo/front-end-services";
import {
  newClientId,
  NO_CATALOGUE_MATCH_GROUP,
  registerToSidebarEntries
} from "@repo/front-end-utils";

// src/createRequestSequencer.ts
function createRequestSequencer() {
  let latest = 0;
  return {
    next: () => latest += 1,
    isLatest: (token) => latest !== 0 && token === latest
  };
}
async function runLatestWins(sequencer2, fetchFn, applyFn) {
  const token = sequencer2.next();
  const result = await fetchFn();
  if (sequencer2.isLatest(token)) {
    applyFn(result);
  }
}

// src/stores/EquipmentCatalogStore.ts
var resolveRegisterScope = (filter) => {
  const { twinId } = filter;
  if (twinId != null && Number.isInteger(twinId) && twinId > 0) {
    return { key: `twin:${twinId}`, request: { twinId } };
  }
  return null;
};
var sequencer = createRequestSequencer();
var IDEMPOTENT_RETRY_TIMEOUT_MS = 1e4;
var newIdempotencyKey = () => (
  // A bare uuid, or `drop-<time>-<a>-<b>` where `randomUUID` is absent — the shape the server's
  // `[A-Za-z0-9._~:-]{16,200}` accepts; the body is `newClientId`'s, shared with every client id.
  newClientId({ uuidPrefix: "", fallbackPrefix: "drop-" })
);
var GATEWAY_STATUS = /\((502|503|504)\)$/;
var isOutcomeUnknown = (error) => {
  if (error instanceof TypeError) return true;
  if (!(error instanceof Error)) return false;
  if (error.name === "TimeoutError" || error.name === "AbortError" || error.name === REPLY_UNREADABLE) {
    return true;
  }
  if (error.message === OFFLINE_MESSAGE) return true;
  return GATEWAY_STATUS.test(error.message);
};
var inFlight = /* @__PURE__ */ new Map();
var inFlightAborts = /* @__PURE__ */ new Map();
var abortRead = (key) => {
  inFlightAborts.get(key)?.abort();
  inFlightAborts.delete(key);
};
var abortAllReads = () => {
  inFlightAborts.forEach((controller) => controller.abort());
  inFlightAborts.clear();
};
var writtenSinceLoadIssued = /* @__PURE__ */ new Map();
var RegisterWriteRefused = class _RegisterWriteRefused extends Error {
  constructor(reason, message) {
    super(message);
    this.reason = reason;
    this.name = "RegisterWriteRefused";
    Object.setPrototypeOf(this, _RegisterWriteRefused.prototype);
  }
};
var TWIN_CHANGED_MESSAGE = "The selected twin changed while this was being recorded. The asset was added to the register of the twin you dragged it on.";
var NO_REGISTER_CONTEXT_MESSAGE = "Select a twin before placing equipment \u2014 every placement is recorded in that twin\u2019s Asset Register.";
function buildLookup(assets) {
  const m = /* @__PURE__ */ new Map();
  for (const a of assets) m.set(a.entity_bid, a);
  return m;
}
var NO_PRODUCT_LINE = "No Product Line";
var siteAssetGroupKeys = (a) => ({
  manufacturer: a.manufacturer_name || "Unknown",
  productLine: a.product_line || NO_PRODUCT_LINE
});
function byManufacturer(a, b) {
  if (a === b) return 0;
  if (a === NO_CATALOGUE_MATCH_GROUP) return -1;
  if (b === NO_CATALOGUE_MATCH_GROUP) return 1;
  return a.localeCompare(b);
}
function groupSiteAssets(assets) {
  const map = /* @__PURE__ */ new Map();
  for (const a of assets) {
    const { manufacturer: mfr, productLine: line } = siteAssetGroupKeys(a);
    if (!map.has(mfr)) map.set(mfr, /* @__PURE__ */ new Map());
    const lm = map.get(mfr);
    if (!lm.has(line)) lm.set(line, []);
    lm.get(line).push(a);
  }
  return Array.from(map.entries()).sort(([a], [b]) => byManufacturer(a, b)).map(([manufacturer, lm]) => ({
    manufacturer,
    productLines: Array.from(lm.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([productLine, assets2]) => ({ productLine, assets: assets2 }))
  }));
}
function filterGroups(groups, query) {
  const q = query.toLowerCase();
  return groups.map((g) => {
    const lines = g.productLines.map((pl) => ({
      ...pl,
      assets: pl.assets.filter((a) => {
        if (q) {
          return a.entity_bid.toLowerCase().includes(q) || a.model_name.toLowerCase().includes(q) || a.product_line.toLowerCase().includes(q) || a.manufacturer_name.toLowerCase().includes(q) || (a.asset_name?.toLowerCase().includes(q) ?? false);
        }
        return true;
      })
    })).filter((pl) => pl.assets.length > 0);
    return lines.length ? { ...g, productLines: lines } : null;
  }).filter(Boolean);
}
var REGISTER_READ_FAILED_MESSAGE = "The asset register could not be read.";
var projectRegisterRows = (rows, searchQuery) => {
  const siteAssets = registerToSidebarEntries(rows);
  const grouped = groupSiteAssets(siteAssets);
  return {
    registerRows: rows,
    siteAssets,
    assetLookup: buildLookup(siteAssets),
    grouped,
    filteredGrouped: filterGroups(grouped, searchQuery)
  };
};
var registerReadState = (state, filter) => {
  const scope = resolveRegisterScope(filter);
  if (!scope) return "no-twin";
  if (state.error !== null) return "error";
  if (state.isLoading || state.loadedKey !== scope.key) return "loading";
  return "ready";
};
var useEquipmentCatalogStore = create2((set, get) => ({
  registerRows: [],
  siteAssets: [],
  assetLookup: /* @__PURE__ */ new Map(),
  grouped: [],
  filteredGrouped: [],
  searchQuery: "",
  isLoading: false,
  error: null,
  loadedKey: null,
  registerContext: null,
  loadRegisterAssets: async (baseUrl, getAccessToken, filter, options) => {
    const scope = resolveRegisterScope(filter);
    const nextKey = scope?.key ?? null;
    const context = get().registerContext;
    const displayedKey = get().loadedKey ?? (context ? resolveRegisterScope({ twinId: context.twinId })?.key ?? null : null);
    if (displayedKey !== null && displayedKey !== nextKey) {
      set({ ...projectRegisterRows([], get().searchQuery), loadedKey: null });
    }
    if (!scope) {
      sequencer.next();
      inFlight.clear();
      abortAllReads();
      writtenSinceLoadIssued = /* @__PURE__ */ new Map();
      set({ registerContext: null, error: null, isLoading: false });
      return;
    }
    const { key, request } = scope;
    set({ registerContext: { baseUrl, getAccessToken, twinId: request.twinId } });
    for (const running2 of Array.from(inFlight.keys())) {
      if (running2 !== key) {
        inFlight.delete(running2);
        abortRead(running2);
      }
    }
    const running = inFlight.get(key);
    if (running && !options?.afterWrite) return running;
    if (running) abortRead(key);
    if (!options?.afterWrite) writtenSinceLoadIssued = /* @__PURE__ */ new Map();
    const abort = new AbortController();
    inFlightAborts.set(key, abort);
    set({ isLoading: true, error: null });
    const load = runLatestWins(
      sequencer,
      /*
       * `request`, not a second copy of the usability test: the scope was decided once, above, and
       * `key` is derived from that same decision.
       *
       * The token is asked for HERE, inside the producer, rather than before the synchronous
       * prologue — awaiting it earlier would move the write-context capture, the in-flight join
       * and the stale-register drop after an await and silently reopen every hazard they exist to
       * close. A token failure lands in the same `catch` as a transport failure, which is correct:
       * both mean "the register could not be read".
       */
      async () => getTwinAssetsOrThrow(baseUrl, await getAccessToken(), request, abort.signal),
      (rows) => {
        const merged = rows.map((r) => writtenSinceLoadIssued.get(r.id)?.row ?? r);
        const seen = new Set(rows.map((r) => r.id));
        writtenSinceLoadIssued.forEach((written) => {
          if (written.created && !seen.has(written.row.id)) merged.push(written.row);
        });
        set((state) => ({
          ...projectRegisterRows(merged, state.searchQuery),
          isLoading: false,
          loadedKey: key,
          error: null
        }));
      }
    ).catch((err) => {
      const e = err;
      if (inFlight.get(key) !== load) return;
      console.error(
        "Error reading the asset register:",
        key,
        e?.response?.status ?? "(no status)",
        e?.message ?? String(err)
      );
      set({ error: REGISTER_READ_FAILED_MESSAGE, isLoading: false });
    }).finally(() => {
      if (inFlight.get(key) === load) inFlight.delete(key);
      if (inFlightAborts.get(key) === abort) inFlightAborts.delete(key);
      if (inFlight.size === 0 && get().isLoading && get().loadedKey !== key) {
        set({ isLoading: false });
      }
    });
    inFlight.set(key, load);
    return load;
  },
  forgetRegisterWrites: (ids) => {
    ids.forEach((id) => writtenSinceLoadIssued.delete(id));
  },
  createRegisterAsset: async (input) => {
    const context = get().registerContext;
    if (!context) throw new RegisterWriteRefused("no-twin-context", NO_REGISTER_CONTEXT_MESSAGE);
    const idempotencyKey = input.idempotencyKey ?? newIdempotencyKey();
    const attempt = async (timeoutMs) => {
      const token = await context.getAccessToken();
      return await createTwinAsset(
        context.baseUrl,
        token,
        {
          twinId: context.twinId,
          name: input.name,
          /*
           * OMITTED, not `null`, when there is none.
           *
           * `readReferenceId` treats absent, `null` and blank identically today, so this is
           * currently a distinction without a difference — and it is written this way so it
           * stays one. The four catalogue columns, `has_ref_data` and the capacity default are
           * the SERVER's (`catalogueFieldsFor`); a body naming any of them is refused with the
           * CSV's own sentence, which is the whole point of feature 8. This payload therefore
           * carries the twin, the name, and AT MOST ONE identity — never a column the server
           * owns, and never both identities.
           */
          ...input.referenceId ? { reference_id: input.referenceId } : {},
          /*
           * The live-entity identity and its model (`dgraph-drift-into-register`, ledger row 80).
           *
           * `!== undefined` on both, never truthiness, for the reason the two spreads above
           * record: this store is a TRANSPORT, and `modelFile: null` is a REAL value — it is
           * what an entity with no 3D model carries, and four of UGG-FIN's ten have none. Under
           * truthiness that null would be dropped and the row would be created with the key
           * absent, which is a different request.
           *
           * Spread as a PAIR rather than independently, and that is the only thing enforcing
           * the coupling on this side: `modelFile` without `entityCode` is a body the route
           * refuses with a 400, and the wire type does NOT make it a compile error (see
           * `CreateTwinAssetPayload`'s note on the union that was tried and abandoned). Keeping
           * them together here means a future edit cannot separate them by accident.
           */
          ...input.entityCode !== void 0 ? {
            entityCode: input.entityCode,
            ...input.modelFile !== void 0 ? { modelFile: input.modelFile } : {}
          } : {}
        },
        timeoutMs,
        idempotencyKey
      );
    };
    let created;
    try {
      created = await attempt(MIDDLEWARE_REQUEST_TIMEOUT_MS);
    } catch (error) {
      if (!isOutcomeUnknown(error)) throw error;
      try {
        created = await attempt(IDEMPOTENT_RETRY_TIMEOUT_MS);
      } catch (retryError) {
        console.error("Asset Register create retry also failed:", { first: error, retryError });
        throw error;
      }
    }
    const contextNow = get().registerContext;
    if (contextNow?.twinId !== context.twinId) {
      throw new RegisterWriteRefused("twin-changed", TWIN_CHANGED_MESSAGE);
    }
    if (get().registerRows.some((row) => row.id === created.id)) return created;
    writtenSinceLoadIssued.set(created.id, { row: created, created: true });
    set((state) => projectRegisterRows([...state.registerRows, created], state.searchQuery));
    return created;
  },
  updateRegisterAsset: async (assetId, fields) => {
    const context = get().registerContext;
    if (!context) throw new RegisterWriteRefused("no-twin-context", NO_REGISTER_CONTEXT_MESSAGE);
    const token = await context.getAccessToken();
    const updated = await updateTwinAsset(context.baseUrl, token, assetId, fields);
    const contextNow = get().registerContext;
    if (contextNow?.twinId !== context.twinId) {
      throw new RegisterWriteRefused("twin-changed", TWIN_CHANGED_MESSAGE);
    }
    writtenSinceLoadIssued.set(updated.id, { row: updated, created: false });
    set(
      (state) => projectRegisterRows(
        state.registerRows.map((rowNow) => rowNow.id === updated.id ? updated : rowNow),
        state.searchQuery
      )
    );
    return updated;
  },
  setSearchQuery: (q) => set({ searchQuery: q, filteredGrouped: filterGroups(get().grouped, q) }),
  getAssetByBid: (bid) => get().assetLookup.get(bid),
  /**
   * Reset everything, including the write capability.
   *
   * Called on unmount by the host that owns a register context — the onboarding wizard
   * (`OnboardingPage`); the admin planner was the other until it was removed — since
   * `wizard-register-on-store`.
   * It had no production caller at all before that, which made it a mitigation that read as
   * present and was not: the store is module-scoped, so a torn-down planner left both a twin's
   * rows and a live write capability standing.
   *
   * The HOST, not `EquipmentSidebar`. The sidebar unmounts whenever the operator leaves the wizard's
   * Floor Plan step, and the Equipment step, the completion ticks and the Launch summary now read
   * this store — clearing there would wipe the register they are rendering from.
   */
  clear: () => {
    inFlight.clear();
    abortAllReads();
    writtenSinceLoadIssued = /* @__PURE__ */ new Map();
    sequencer.next();
    set({
      ...projectRegisterRows([], ""),
      searchQuery: "",
      loadedKey: null,
      error: null,
      // A torn-down planner must not be able to write to the twin it just left. `clear()` is
      // the "reset everything" action, and a surviving context is a live write capability.
      registerContext: null,
      // Reset explicitly rather than relying on an in-flight load's `finally` to do it.
      // That cleanup does currently fire, so this is not a live bug — but `clear()` is the
      // "reset everything" action and leaving one field to be tidied by a promise nobody
      // is awaiting is a trap: any future path that sets `isLoading` without a matching
      // in-flight entry would strand the spinner (Copilot, PR #1132).
      isLoading: false
    });
  }
}));

// src/stores/activeFloorPair.ts
import { subscribeWithSelector as subscribeWithSelector2 } from "zustand/middleware";
import { asArray } from "@repo/front-end-utils";
var floorsOf = (layout) => asArray(layout?.floors);
var floorIdAt = (layout, index) => {
  const id = floorsOf(layout)[index]?.id;
  return typeof id === "number" ? id : null;
};
var followFloor = (layout, index, id) => {
  if (id === null || floorIdAt(layout, index) === id) return index;
  const at = floorsOf(layout).findIndex((floor) => floor?.id === id);
  return at < 0 ? index : at;
};
function withFloorPair(state, patch) {
  const writesIndex = "activeFloorIndex" in patch;
  if (!writesIndex && (!("layout" in patch) || patch.layout === state.layout)) return patch;
  const layout = "layout" in patch ? patch.layout : state.layout;
  const activeFloorIndex = writesIndex ? patch.activeFloorIndex : followFloor(layout, state.activeFloorIndex, state.activeFloorId);
  return { ...patch, activeFloorIndex, activeFloorId: floorIdAt(layout, activeFloorIndex) };
}
function followHeldFloor(before, after, activeFloorIndex) {
  if (before === after) return;
  const editor = useEditorStore.getState();
  const held = editor.heldSelectionFloor;
  if (held === null) return;
  const next = followFloor(after, held, floorIdAt(before, held));
  if (next !== held) editor.setHeldSelectionFloor(next === activeFloorIndex ? null : next);
}
var activeFloorPair = (config) => (set, get, api) => {
  const paired = (raw) => (partial, replace, ...rest) => {
    const before = get();
    const patch = typeof partial === "function" ? partial(before) : partial;
    const next = Object.is(patch, before) ? patch : withFloorPair(before, patch);
    if ("layout" in next) {
      followHeldFloor(
        before.layout,
        next.layout ?? null,
        next.activeFloorIndex ?? before.activeFloorIndex
      );
    }
    raw(next, replace, ...rest);
  };
  api.setState = paired(api.setState);
  return config(paired(set), get, api);
};
var subscribeWithFloorPair = (config) => subscribeWithSelector2(activeFloorPair(config));

// src/stores/draftBasis.ts
var layoutWithDraftBasis = (layout, basis) => {
  const { observerDraft: _legacy, ...rest } = layout;
  return basis.editedAt ? {
    ...rest,
    draftBasis: basis,
    observerDraft: { editedAt: basis.editedAt, basedOn: "unknown" }
  } : { ...rest, draftBasis: basis };
};

// src/stores/FloorplanStore.ts
var normalisedLayout = (layout) => ensurePlanItemIds(
  normaliseFloorLevels(normaliseFloorPlacement(foldLegacyBackground(structuredClone(layout))))
);
var copiedWalls = (walls) => JSON.parse(JSON.stringify(walls)).map((wall) => ({
  ...wall,
  id: newWallId()
}));
var normaliseEntityCodeUpdate = (updates) => typeof updates.entityCode === "string" ? (
  // `entityIdentityKey`, the one definition of this fold — not a second hand-rolled copy of it.
  // Fixed alongside the settle below, under the rule that the same problem in adjacent code is
  // repaired now rather than filed.
  { ...updates, entityCode: entityIdentityKey(updates.entityCode) }
) : updates;
function removeFloorAt(state, index, record) {
  if (!state.layout) return {};
  if (state.layout.floors.length <= 1 || index < 0 || index >= state.layout.floors.length)
    return {};
  const cascaded = cascadeFloorCameras(state.layout, state.layout.floors[index]);
  const floors = cascaded.floors;
  const updatedFloors = floors.filter((_, i) => i !== index).map((floor, i) => floor.level === i ? floor : { ...floor, level: i });
  let activeFloorIndex = state.activeFloorIndex;
  if (index < activeFloorIndex) activeFloorIndex -= 1;
  else if (index === activeFloorIndex) activeFloorIndex = Math.min(index, updatedFloors.length - 1);
  useEditorStore.getState().clearSelection();
  useEditorStore.getState().forgetFloor(floors[index].id);
  const removedFloor = floors[index];
  return withRemovedFromComposites(
    {
      layout: {
        ...cascaded,
        floors: updatedFloors,
        floorIdHighWater: nextFloorId(cascaded) - 1,
        ...record ? { removedFloors: [...cascaded.removedFloors ?? [], record] } : {}
      },
      activeFloorIndex,
      isDirty: true
    },
    floorRemovedRefs(removedFloor)
  );
}
var isFloorMove = (floorCount, from, to) => Number.isInteger(from) && Number.isInteger(to) && from !== to && from >= 0 && to >= 0 && from < floorCount && to < floorCount;
function moveFloorAt(state, from, to) {
  if (!state.layout || !isFloorMove(state.layout.floors.length, from, to)) return {};
  const moved = [...state.layout.floors];
  const [floor] = moved.splice(from, 1);
  moved.splice(to, 0, floor);
  const floors = moved.map((f, i) => f.level === i ? f : { ...f, level: i });
  return { layout: { ...state.layout, floors }, isDirty: true };
}
function withRemovedFromComposites(next, removed) {
  if (!next.layout) return next;
  const pruned = pruneCompositesForRemovedRefs(next.layout.composites, removed);
  return pruned.changed ? { ...next, layout: { ...next.layout, composites: pruned.composites } } : next;
}
function updateFloorDesign(state, floorIndex, updater) {
  if (!state.layout) return {};
  const floor = state.layout.floors[floorIndex];
  if (!floor) return {};
  const design = findLatestDesign(floor.designs);
  const designIndex = floor.designs.indexOf(design);
  const updatedDesign = updater({ ...design });
  const updatedDesigns = [...floor.designs];
  updatedDesigns[designIndex] = updatedDesign;
  const updatedFloors = [...state.layout.floors];
  updatedFloors[floorIndex] = {
    ...floor,
    designs: updatedDesigns
  };
  return {
    layout: { ...state.layout, floors: updatedFloors },
    isDirty: true
  };
}
var floorFor = (state, options) => options?.floorIndex ?? state.activeFloorIndex;
function liveOnFloor(layout, floorIndex, refid, kinds) {
  const floor = layout?.floors?.[floorIndex];
  if (!floor) return false;
  const design = findLatestDesign(floor.designs);
  return kinds.some(
    (kind) => asArray2(design?.[kind]).some(
      (e) => !!e && e.refid === refid && isLive(e)
    )
  );
}
function releaseLostHold(layout) {
  const editor = useEditorStore.getState();
  const held = editor.heldSelectionFloor;
  if (held === null) return;
  const [id] = editor.selectedEntityIds;
  if (editor.selectedEntityIds.length !== 1 || !liveOnFloor(layout, held, id, ["items", "surfaces"])) {
    editor.clearSelection();
  }
}
function updateDesign(state, updater) {
  return updateFloorDesign(state, state.activeFloorIndex, updater);
}
function planSurfaceRemoval(design, refids, promote) {
  const removed = new Set(refids);
  const after = reparentFromDrawing(
    {
      ...design,
      surfaces: design.surfaces.filter((s) => !removed.has(s.refid)),
      // Only where the design has observers — a design without the key keeps that shape.
      ...design.observers ? { observers: pruneObserversForRemovedRefs(design.observers, removed) } : {}
    },
    {
      // Their children and whatever they enclosed are re-derived, so nothing is left pointing at a
      // zone that no longer exists (how Wilmslow's dangling parents arose).
      childrenOf: removed,
      outlines: design.surfaces.filter((s) => removed.has(s.refid)).map((s) => s.poly)
    }
  );
  return promote ? promoteAfterRemoval(design, after, removed, promote) : { ok: true, design: after };
}
var insideAny = (point, outlines) => !!point && outlines.some((o) => usablePoly(o) && pointInPolygon(point, o));
function reparentFromDrawing(design, scope) {
  const outlines = scope.outlines ?? [];
  const surfaces = design.surfaces ?? [];
  const live = firstLiveSurfaces(surfaces);
  const surfaceAffected = (x) => !!scope.surfaces?.has(x.refid) || !!x.parentSurfaceBid && !!scope.childrenOf?.has(x.parentSurfaceBid) || insideAny(polygonInteriorPoint(x.poly), outlines);
  const itemAffected = (x) => !!scope.items?.has(x.refid) || !!x.parentSpaceBid && !!scope.childrenOf?.has(x.parentSpaceBid) || typeof x.x === "number" && typeof x.y === "number" && insideAny({ x: x.x, y: x.y }, outlines);
  let surfacesChanged = false;
  const nextSurfaces = surfaces.map((x) => {
    if (!isLive(x) || !surfaceAffected(x)) return x;
    const parent = spatialParentOfSurface(x, live) ?? void 0;
    if ((x.parentSurfaceBid || void 0) === parent && !x.parentChosen) return x;
    surfacesChanged = true;
    const { parentSurfaceBid: _drop, parentChosen: _chosen, parentKeptOver: _kept, ...rest } = x;
    return parent ? { ...rest, parentSurfaceBid: parent } : rest;
  });
  let itemsChanged = false;
  const nextItems = (design.items ?? []).map((x) => {
    if (!isLive(x) || !itemAffected(x)) return x;
    const parent = spatialParentOfItem(x, live) ?? void 0;
    if ((x.parentSpaceBid || void 0) === parent && !x.parentChosen) return x;
    itemsChanged = true;
    const { parentSpaceBid: _drop, parentChosen: _chosen, parentKeptOver: _kept, ...rest } = x;
    return parent ? { ...rest, parentSpaceBid: parent } : rest;
  });
  if (!surfacesChanged && !itemsChanged) return design;
  return {
    ...design,
    ...surfacesChanged ? { surfaces: nextSurfaces } : {},
    ...itemsChanged ? { items: nextItems } : {}
  };
}
function cleanAdjustment(patch) {
  const out = {};
  if (typeof patch.locked === "boolean") out.locked = patch.locked;
  if ("scaleFromM" in patch) {
    const length = patch.scaleFromM;
    if (length === void 0) out.scaleFromM = void 0;
    else if (Number.isFinite(length) && length > 0) out.scaleFromM = length;
  }
  for (const [key, value] of Object.entries(patch)) {
    if (key === "locked" || key === "scaleFromM") continue;
    if (typeof value !== "number" || !Number.isFinite(value)) continue;
    if (key === "scale" && value <= 0) continue;
    if (key === "opacity" && (value <= 0 || value > 1)) continue;
    if (key === "contrast" && value <= 0) continue;
    out[key] = key === "contrast" ? underlayContrast({ contrast: value }) : value;
  }
  return out;
}
function seedFloorDesign(state, floorIndex) {
  const layout = state.layout;
  const floor = layout?.floors[floorIndex];
  const designs = Array.isArray(floor?.designs) ? floor.designs : [];
  if (!layout || !floor || designs.includes(findLatestDesign(designs))) return state;
  return {
    ...state,
    layout: {
      ...layout,
      floors: layout.floors.map(
        (f, i) => i === floorIndex ? { ...f, designs: createEmptyFloor(f.level, f.id).designs } : f
      )
    }
  };
}
var clampIndex = (length, index) => Math.max(0, Math.min(length, Math.trunc(index)));
function insertAt(list, item, index) {
  const at = clampIndex(list.length, index);
  return [...list.slice(0, at), item, ...list.slice(at)];
}
function updateUnderlays(state, underlayId, rewrite) {
  const index = state.layout?.floors.findIndex(
    (f) => findLatestDesign(f.designs)?.underlays?.some((u) => u.id === underlayId)
  ) ?? -1;
  if (index < 0) return {};
  return updateFloorDesign(state, index, (d) => ({ ...d, underlays: rewrite(d.underlays ?? []) }));
}
var withHandCapacity = (holder, capacity, reviewedContents) => {
  const { capacity: _capacity, capacityReviewedContents: _reviewed, ...rest } = holder;
  if (capacity === void 0) return rest;
  return {
    ...rest,
    capacity,
    ...isEntityCapacity(reviewedContents) ? { capacityReviewedContents: reviewedContents } : {}
  };
};
var useFloorplanStore = create3()(
  devtools2(
    subscribeWithFloorPair((set, get) => ({
      layout: null,
      activeFloorIndex: 0,
      activeFloorId: null,
      isDirty: false,
      twinBID: null,
      orgBID: null,
      twinName: null,
      orgName: null,
      unverifiedMints: [],
      collidingMints: [],
      loadLayout: (layout, twinBID, orgBID, twinName, orgName, opts) => {
        useEditorStore.getState().selectFloor(null);
        useEditorStore.getState().setHeldSelectionFloor(null);
        set((state) => ({
          // Nothing writes `backgroundImage` after this.
          layout: normalisedLayout(layout),
          twinBID,
          orgBID,
          twinName: twinName ?? null,
          orgName: orgName ?? null,
          isDirty: opts?.markDirty ?? false,
          activeFloorIndex: 0,
          /*
           * The gate is keyed on the TWIN, so loading a different twin's plan clears it and
           * loading the same twin's again does not.
           *
           * That asymmetry is the point. The wizard re-loads the same twin's layout on ordinary
           * navigation, and clearing there would defeat the gate exactly as unmounting used to;
           * carrying another twin's unverified mints forward would refuse a save for a reason
           * belonging to a different twin.
           */
          ...state.twinBID === twinBID ? {} : {
            unverifiedMints: [],
            collidingMints: []
          }
        }));
      },
      recordUnverifiedMint: (identity) => set(
        (state) => identity === "" || state.unverifiedMints.includes(identity) ? {} : { unverifiedMints: [...state.unverifiedMints, identity] }
      ),
      settleUnverifiedMints: (liveBids) => set((state) => {
        const held = new Set(Array.from(liveBids).map(entityIdentityKey));
        const collided = state.unverifiedMints.filter((m) => held.has(entityIdentityKey(m)));
        return {
          unverifiedMints: [],
          // Additive: a collision found in an earlier settle is not forgotten by a later one.
          collidingMints: Array.from(/* @__PURE__ */ new Set([...state.collidingMints, ...collided]))
        };
      }),
      // Deliberately narrow: the layout and the dirty flag, nothing else. Anything wider would
      // make this a second `loadLayout` and re-introduce the session re-keying it exists to avoid.
      replaceLayout: (layout, options) => {
        const selected = useEditorStore.getState().selectedFloorId;
        if (selected !== null && !asArray2(layout?.floors).some((f) => f?.id === selected)) {
          useEditorStore.getState().selectFloor(null);
        }
        set({
          layout: normalisedLayout(layout),
          ...options?.keepDirty ? {} : { isDirty: true },
          ...options?.activeFloorIndex === void 0 ? {} : { activeFloorIndex: options.activeFloorIndex }
        });
        releaseLostHold(get().layout);
      },
      reset: () => {
        useEditorStore.getState().selectFloor(null);
        useEditorStore.getState().setHeldSelectionFloor(null);
        set({
          layout: null,
          activeFloorIndex: 0,
          isDirty: false,
          twinBID: null,
          orgBID: null,
          twinName: null,
          orgName: null,
          // `reset` tears the whole session down — there is no plan left to refuse to save.
          unverifiedMints: [],
          collidingMints: []
        });
      },
      setActiveFloor: (index, opts) => {
        const from = get().activeFloorIndex;
        if (from === index) return;
        const editor = useEditorStore.getState();
        const held = editor.heldSelectionFloor;
        if (held !== null && (index === held || opts?.keepSelection)) {
          if (index === held) editor.setHeldSelectionFloor(null);
          set({ activeFloorIndex: index });
          return;
        }
        const ids = editor.selectedEntityIds;
        if (opts?.keepSelection && held === null && ids.length === 1 && liveOnFloor(get().layout, from, ids[0], ["items", "surfaces"])) {
          editor.setHeldSelectionFloor(from);
          set({ activeFloorIndex: index });
          return;
        }
        editor.clearSelection();
        set({ activeFloorIndex: index });
      },
      addFloor: (opts) => set((state) => {
        if (!state.layout) return {};
        const floors = state.layout.floors;
        const level = floors.length;
        const id = nextFloorId(state.layout);
        const index = buildTwinNameIndex(
          state.layout,
          useEquipmentCatalogStore.getState().registerRows
        );
        const wanted = opts?.name?.trim();
        const name = wanted ? isNameTaken(wanted, index) ? nextFreeName(wanted, index) : wanted : nameForNewcomer(defaultFloorName(level), "floor", index).name;
        const newFloor = createEmptyFloor(level, id, { name });
        const source = opts?.source;
        if (source?.kind === "footprint") {
          const from = floors.find((f) => f.id === source.floorId);
          if (from) {
            const walls = findLatestDesign(from.designs).walls ?? [];
            if (walls.length > 0) newFloor.designs[0].walls = copiedWalls(walls);
            if (from.placement) newFloor.placement = { ...from.placement };
            if (typeof from.height === "number") newFloor.height = from.height;
          }
        } else if (source?.kind === "walls") {
          const from = floors.find((f) => f.id === source.floorId);
          const walls = from ? findLatestDesign(from.designs).walls ?? [] : [];
          if (walls.length > 0) newFloor.designs[0].walls = copiedWalls(walls);
        } else if (source?.kind === "blank") {
          const below = floors[floors.length - 1];
          if (below?.placement) newFloor.placement = { ...below.placement };
        }
        const updatedFloors = [...floors, newFloor];
        useEditorStore.getState().clearSelection();
        return {
          layout: { ...state.layout, floors: updatedFloors, floorIdHighWater: id },
          activeFloorIndex: updatedFloors.length - 1,
          isDirty: true
        };
      }),
      removeLaunchedFloor: (index) => {
        const layout = get().layout;
        if (!layout || layout.floors.length <= 1 || index < 0 || index >= layout.floors.length)
          return null;
        const record = buildFloorRemovalRecord(layout, index);
        if (!record) return null;
        set((state) => removeFloorAt(state, index, record));
        return record;
      },
      restoreFloor: (record) => {
        const layout = get().layout;
        if (!layout) return null;
        const result = restoreFloorIntoLayout(
          layout,
          record,
          useEquipmentCatalogStore.getState().registerRows
        );
        if (!result) return null;
        if ("blocked" in result) return result;
        useEditorStore.getState().clearSelection();
        set({ layout: result.layout, activeFloorIndex: result.index, isDirty: true });
        return result;
      },
      removeFloor: (index) => set((state) => removeFloorAt(state, index)),
      moveFloor: (from, to) => set((state) => moveFloorAt(state, from, to)),
      renameFloor: (index, name) => set((state) => {
        const floor = state.layout?.floors[index];
        if (!state.layout || !floor) return {};
        const updatedFloors = [...state.layout.floors];
        updatedFloors[index] = { ...floor, name };
        return { layout: { ...state.layout, floors: updatedFloors }, isDirty: true };
      }),
      setFloorHeight: (index, height) => set((state) => {
        const floor = state.layout?.floors[index];
        if (!state.layout || !floor) return {};
        const updatedFloors = [...state.layout.floors];
        updatedFloors[index] = { ...floor, height };
        return { layout: { ...state.layout, floors: updatedFloors }, isDirty: true };
      }),
      setFloorCapacity: (index, capacity, reviewedContents) => set((state) => {
        const floor = state.layout?.floors[index];
        if (!state.layout || !floor) return {};
        if (capacity !== void 0 && !isEntityCapacity(capacity)) return {};
        const next = withHandCapacity(floor, capacity, reviewedContents);
        if (next.capacity === floor.capacity && next.capacityReviewedContents === floor.capacityReviewedContents) {
          return {};
        }
        const updatedFloors = [...state.layout.floors];
        updatedFloors[index] = next;
        return { layout: { ...state.layout, floors: updatedFloors }, isDirty: true };
      }),
      setTwinCapacity: (capacity, reviewedContents) => set((state) => {
        if (!state.layout) return {};
        if (capacity !== void 0 && !isEntityCapacity(capacity)) return {};
        const next = withHandCapacity(state.layout, capacity, reviewedContents);
        if (next.capacity === state.layout.capacity && next.capacityReviewedContents === state.layout.capacityReviewedContents) {
          return {};
        }
        return { layout: next, isDirty: true };
      }),
      setFloorPlacement: (floorId, placement) => set((state) => {
        const index = state.layout?.floors.findIndex((f) => f.id === floorId) ?? -1;
        const floor = index >= 0 ? state.layout?.floors[index] : void 0;
        if (!state.layout || !floor) return {};
        const clean = cleanPlacement(placement);
        const current = placementOf(floor);
        if (samePlacement(clean, current)) return {};
        const { placement: _old, ...unplaced } = floor;
        const updatedFloors = [...state.layout.floors];
        updatedFloors[index] = isIdentityPlacement(clean) ? unplaced : { ...unplaced, placement: clean };
        return { layout: { ...state.layout, floors: updatedFloors }, isDirty: true };
      }),
      markClean: () => set({ isDirty: false }),
      setDirty: (dirty) => set({ isDirty: dirty }),
      addItem: (item) => set(
        (state) => updateDesign(
          state,
          (d) => reparentFromDrawing(
            { ...d, items: [...d.items, normaliseEntityCodeUpdate(item)] },
            { items: /* @__PURE__ */ new Set([item.refid]) }
          )
        )
      ),
      updateItem: (refid, updates, options) => set(
        (state) => options?.floorIndex != null && !liveOnFloor(state.layout, options.floorIndex, refid, ["items"]) ? {} : updateFloorDesign(state, floorFor(state, options), (d) => {
          const next = {
            ...d,
            items: d.items.map(
              (i) => i.refid === refid ? { ...i, ...normaliseEntityCodeUpdate(updates) } : i
            )
          };
          return "x" in updates || "y" in updates ? reparentFromDrawing(next, { items: /* @__PURE__ */ new Set([refid]) }) : next;
        })
      ),
      /*
       * ⛔ **A deletion reaches every composite and takes its camera regions with it**
       * (`complex-measures-in-configuration-parent-space-composites`; Rob's rulings 184 and
       * 2026-10-01). The removed thing leaves every total that names it — hand-made included — a
       * total emptied by that goes, and the observers drawn on it go too. One `set`, so one undo
       * restores all of it.
       */
      removeItem: (refid, options) => {
        set(
          (state) => (
            // A card on a floor where the machine has gone writes nothing (`plan-fly-to`, R4.5).
            options?.floorIndex != null && !liveOnFloor(state.layout, options.floorIndex, refid, ["items"]) ? {} : withRemovedFromComposites(
              updateFloorDesign(state, floorFor(state, options), (d) => ({
                ...d,
                items: d.items.filter((i) => i.refid !== refid),
                // Only where the design has observers — a design without the key keeps that shape.
                ...d.observers ? { observers: pruneObserversForRemovedRefs(d.observers, /* @__PURE__ */ new Set([refid])) } : {}
              })),
              { refids: /* @__PURE__ */ new Set([refid]) }
            )
          )
        );
        releaseLostHold(get().layout);
      },
      addSurface: (surface) => set(
        (state) => updateDesign(
          state,
          (d) => reparentFromDrawing(
            { ...d, surfaces: [...d.surfaces, normaliseEntityCodeUpdate(surface)] },
            { surfaces: /* @__PURE__ */ new Set([surface.refid]), outlines: [surface.poly] }
          )
        )
      ),
      updateSurface: (refid, updates, options) => set(
        (state) => options?.floorIndex != null && !liveOnFloor(state.layout, options.floorIndex, refid, ["surfaces"]) ? {} : updateFloorDesign(state, floorFor(state, options), (d) => {
          const before = d.surfaces.find((s) => s.refid === refid);
          const next = {
            ...d,
            surfaces: d.surfaces.map(
              (s) => s.refid === refid ? { ...s, ...normaliseEntityCodeUpdate(updates) } : s
            )
          };
          return "poly" in updates && !options?.deferReparent ? reparentFromDrawing(next, {
            surfaces: /* @__PURE__ */ new Set([refid]),
            childrenOf: /* @__PURE__ */ new Set([refid]),
            outlines: [before?.poly, updates.poly]
          }) : next;
        })
      ),
      reparentAfterReshape: (refid, fromPoly) => set(
        (state) => updateDesign(
          state,
          (d) => reparentFromDrawing(d, {
            surfaces: /* @__PURE__ */ new Set([refid]),
            childrenOf: /* @__PURE__ */ new Set([refid]),
            outlines: [fromPoly, d.surfaces.find((s) => s.refid === refid)?.poly]
          })
        )
      ),
      removeSurface: (refid) => {
        get().removeSurfaces([refid]);
      },
      removeSurfaces: (refids, options) => {
        let outcome = { ok: true };
        set((state) => {
          const floorIndex = floorFor(state, options);
          const design = state.layout?.floors[floorIndex] ? findLatestDesign(state.layout.floors[floorIndex].designs) : null;
          if (!design || !state.layout) return {};
          if (options?.floorIndex != null && !refids.some((id) => liveOnFloor(state.layout, floorIndex, id, ["surfaces"]))) {
            return {};
          }
          const planned = planSurfaceRemoval(design, refids, options?.promote);
          if (!planned.ok) {
            outcome = { ok: false, message: planned.message };
            return {};
          }
          const removed = new Set(refids);
          const next = withRemovedFromComposites(
            updateFloorDesign(state, floorIndex, () => planned.design),
            { refids: removed }
          );
          if (!next.layout) return next;
          let changes = asArray2(next.layout.entityCodeChanges).filter(
            (c) => !!c && !removed.has(c.refid)
          );
          const promotion = "recodes" in planned ? planned : void 0;
          promotion?.recodes.forEach((r) => {
            changes = recordCodeChange(changes, {
              refid: r.refid,
              from: r.from,
              to: r.to,
              live: r.live,
              twinBid: options?.promote?.twinBid
            });
          });
          const { entityCodeChanges: _previous, ...rest } = next.layout;
          outcome = { ok: true, ...promotion ? { promotion } : {} };
          return {
            ...next,
            layout: changes.length > 0 ? { ...rest, entityCodeChanges: changes } : rest
          };
        });
        releaseLostHold(get().layout);
        return outcome;
      },
      addWall: (wall) => set(
        (state) => updateDesign(state, (d) => ({
          ...d,
          walls: [...d.walls, wall]
        }))
      ),
      updateWall: (index, updates) => set(
        (state) => updateDesign(state, (d) => ({
          ...d,
          walls: d.walls.map((w, i) => i === index ? { ...w, ...updates } : w)
        }))
      ),
      moveWallEnds: (ends, to) => set(
        (state) => updateDesign(state, (d) => ({
          ...d,
          walls: d.walls.map((w) => {
            const moved = ends.filter((e) => e.id === w.id);
            if (moved.length === 0) return w;
            const next = { ...w };
            for (const { end } of moved) next[end] = { x: to.x, y: to.y };
            return next;
          })
        }))
      ),
      removeWall: (id) => set(
        (state) => updateDesign(state, (d) => ({
          ...d,
          walls: d.walls.filter((w) => w.id !== id)
        }))
      ),
      addCamera: (camera) => set(
        (state) => updateDesign(state, (d) => ({
          ...d,
          cameras: [...d.cameras ?? [], camera]
        }))
      ),
      updateCamera: (refid, updates) => set(
        (state) => updateDesign(state, (d) => ({
          ...d,
          cameras: (d.cameras ?? []).map((c) => c.refid === refid ? { ...c, ...updates } : c)
        }))
      ),
      /*
       * ⛔ **A camera takes the regions drawn on its feed with it** (`onboarding-polish-delete-cascade`,
       * Rob, 2026-10-05). An observer names its camera by feed, not refid, so every live observer on
       * the camera's feed goes — on every floor — unless another live camera still carries that
       * feed. `observersOnRemovedCamera` is the one rule, and the planner's warning counts with it.
       * One `set`, so one undo restores the camera and its regions together.
       */
      removeCamera: (refid) => set((state) => {
        if (!state.layout) return {};
        const out = removeCameraFromLayout(state.layout, refid);
        return out.layout === state.layout ? {} : { layout: out.layout, isDirty: true };
      }),
      setObservers: (observers) => set((state) => updateDesign(state, (d) => ({ ...d, observers }))),
      setAllFloorObservers: (observersByFloor) => set((state) => {
        if (!state.layout) return {};
        const updatedFloors = state.layout.floors.map((floor, i) => {
          const design = findLatestDesign(floor.designs);
          const designIndex = floor.designs.indexOf(design);
          if (designIndex < 0) return floor;
          const updatedDesigns = [...floor.designs];
          updatedDesigns[designIndex] = { ...design, observers: observersByFloor[i] ?? [] };
          return { ...floor, designs: updatedDesigns };
        });
        return { layout: { ...state.layout, floors: updatedFloors }, isDirty: true };
      }),
      setComposites: (composites, options) => set(
        (state) => state.layout ? {
          layout: { ...state.layout, composites },
          ...options?.keepDirty ? {} : { isDirty: true }
        } : {}
      ),
      setEntityCodeChanges: (changes) => set((state) => {
        if (!state.layout) return {};
        const { entityCodeChanges: _previous, ...rest } = state.layout;
        return {
          layout: changes.length > 0 ? { ...rest, entityCodeChanges: changes } : rest,
          isDirty: true
        };
      }),
      setDraftBasis: (draftBasis) => set(
        (state) => state.layout ? { layout: layoutWithDraftBasis(state.layout, draftBasis) } : {}
      ),
      addUnderlay: (floorId, underlay, at) => set((state) => {
        const index = state.layout?.floors.findIndex((f) => f.id === floorId) ?? -1;
        if (index < 0) return {};
        return updateFloorDesign(seedFloorDesign(state, index), index, (d) => {
          const list = d.underlays ?? [];
          return { ...d, underlays: insertAt(list, underlay, at ?? list.length) };
        });
      }),
      moveUnderlay: (underlayId, toFloorId, at) => set((state) => {
        const floors = state.layout?.floors;
        if (!floors) return {};
        const from = floors.findIndex(
          (f) => findLatestDesign(f.designs)?.underlays?.some((u) => u.id === underlayId)
        );
        const to = floors.findIndex((f) => f.id === toFloorId);
        if (from < 0 || to < 0) return {};
        const source = findLatestDesign(floors[from].designs).underlays ?? [];
        const current = source.findIndex((u) => u.id === underlayId);
        const underlay = source[current];
        const rest = source.filter((u) => u.id !== underlayId);
        if (from === to) {
          const target = clampIndex(rest.length, at);
          if (target === current) return {};
          return updateFloorDesign(state, from, (d) => ({
            ...d,
            underlays: insertAt(rest, underlay, target)
          }));
        }
        const removed = {
          ...state,
          ...updateFloorDesign(state, from, (d) => ({ ...d, underlays: rest }))
        };
        const moved = reexpressUnderlay(
          underlay,
          placementOf(floors[from]),
          placementOf(floors[to])
        );
        return updateFloorDesign(seedFloorDesign(removed, to), to, (d) => ({
          ...d,
          underlays: insertAt(d.underlays ?? [], moved, at)
        }));
      }),
      removeUnderlay: (underlayId) => set(
        (state) => updateUnderlays(state, underlayId, (list) => list.filter((u) => u.id !== underlayId))
      ),
      renameUnderlay: (underlayId, name) => set((state) => {
        const clean = cleanUnderlayName(name);
        if (!clean) return {};
        return updateUnderlays(
          state,
          underlayId,
          (list) => list.map((u) => u.id === underlayId ? { ...u, name: clean } : u)
        );
      }),
      updateUnderlay: (underlayId, patch) => set((state) => {
        const clean = cleanAdjustment(patch);
        const current = state.layout?.floors.flatMap((f) => findLatestDesign(f.designs)?.underlays ?? []).find((u) => u.id === underlayId);
        if (!current) return {};
        const changed = Object.keys(clean).some(
          (k) => k === "contrast" ? underlayContrast(current) !== clean[k] : current[k] !== clean[k]
        );
        if (!changed) return {};
        return updateUnderlays(
          state,
          underlayId,
          (list) => list.map((u) => u.id === underlayId ? { ...u, ...clean } : u)
        );
      }),
      setUnderlaysVisible: (underlayIds, visible) => set((state) => {
        if (!state.layout || underlayIds.length === 0) return {};
        const ids = new Set(underlayIds);
        let changed = false;
        const floors = state.layout.floors.map((floor) => {
          const design = findLatestDesign(floor.designs);
          if (!design?.underlays?.some((u) => ids.has(u.id) && u.visible !== visible)) return floor;
          changed = true;
          const designs = [...floor.designs];
          designs[floor.designs.indexOf(design)] = {
            ...design,
            // Visibility only — a hidden PDF keeps its opacity (ledger row 209).
            underlays: design.underlays.map((u) => ids.has(u.id) ? { ...u, visible } : u)
          };
          return { ...floor, designs };
        });
        return changed ? { layout: { ...state.layout, floors }, isDirty: true } : {};
      }),
      attachStoredUnderlays: (stored) => set((state) => {
        if (!state.layout || stored.length === 0) return {};
        const byId = new Map(stored.map((s) => [s.underlayId, s]));
        let changed = false;
        const floors = state.layout.floors.map((floor) => {
          let floorChanged = false;
          const designs = floor.designs.map((design) => {
            if (!design.underlays?.some((u) => byId.has(u.id))) return design;
            const underlays = design.underlays.map((u) => {
              const s = byId.get(u.id);
              if (!s || u.data === void 0 || u.data !== s.data) return u;
              floorChanged = true;
              const { data: _uploaded, ...rest } = u;
              return { ...rest, imageRef: s.imageRef };
            });
            return { ...design, underlays };
          });
          if (!floorChanged) return floor;
          changed = true;
          return { ...floor, designs };
        });
        return changed ? { layout: { ...state.layout, floors } } : {};
      }),
      getActiveDesign: () => {
        const { layout, activeFloorIndex } = get();
        if (!layout) return null;
        const floor = layout.floors[activeFloorIndex];
        if (!floor) return null;
        return findLatestDesign(floor.designs);
      }
    })),
    { name: "floorplan-store" }
  )
);

// src/stores/selectEntityOnFloor.ts
function selectEntityOnFloor(refid, floorIndex) {
  const plan = useFloorplanStore.getState();
  const switched = floorIndex != null && floorIndex !== plan.activeFloorIndex;
  if (switched) plan.setActiveFloor(floorIndex);
  const editor = useEditorStore.getState();
  editor.selectEntity(refid);
  editor.highlightEntity(refid);
  return switched;
}

// src/stores/flyToOnPlan.ts
import { entityWorldCentre, findLatestDesign as findLatestDesign2 } from "@repo/front-end-utils";
var NOTHING = { moved: false, switched: false };
function flyToOnPlan(refid, options = {}) {
  if (typeof refid !== "string" || refid.trim() === "") return NOTHING;
  const plan = useFloorplanStore.getState();
  const floors = plan.layout?.floors;
  if (!floors) return NOTHING;
  const floorIndex = options.floorIndex ?? plan.activeFloorIndex;
  if (!Number.isInteger(floorIndex) || floorIndex < 0 || floorIndex >= floors.length) {
    return NOTHING;
  }
  const floor = floors[floorIndex];
  if (!floor || !entityWorldCentre(findLatestDesign2(floor.designs), refid)) return NOTHING;
  const switched = floorIndex !== plan.activeFloorIndex;
  const editor = useEditorStore.getState();
  if (options.select) {
    selectEntityOnFloor(refid, floorIndex);
  } else if (switched) {
    plan.setActiveFloor(floorIndex, { keepSelection: true });
    if (useEditorStore.getState().heldSelectionFloor !== null) editor.markHeldFloorShown();
  }
  if (options.glow) editor.markJumped(refid);
  editor.requestFly(refid, { onlyIfHidden: options.onlyIfHidden === true });
  return { moved: true, switched };
}
var NO_SELECTION = Object.freeze([]);
var selectionOnFloorShown = (state) => state.heldSelectionFloor === null ? state.selectedEntityIds : NO_SELECTION;

// src/stores/ScaleCalibrationStore.ts
import { create as create4 } from "zustand";
var useScaleCalibrationStore = create4((set) => ({
  pointA: null,
  pointB: null,
  cursorPos: null,
  showDistanceDialog: false,
  setPointA: (p) => set({ pointA: p }),
  setPointB: (p) => set({ pointB: p }),
  setCursorPos: (p) => set({ cursorPos: p }),
  setShowDistanceDialog: (show) => set({ showDistanceDialog: show }),
  reset: () => set({ pointA: null, pointB: null, cursorPos: null, showDistanceDialog: false })
}));

// src/stores/CameraCatalogStore.ts
import { create as create5 } from "zustand";
import { describeRequestError, getCamerasOrThrow } from "@repo/front-end-services";
var useCameraCatalogStore = create5((set, get) => ({
  cameras: [],
  isLoading: false,
  error: null,
  loaded: false,
  fetchCameras: async (baseUrl, accessToken) => {
    set({ isLoading: true, error: null });
    try {
      const cameras = await getCamerasOrThrow(baseUrl, accessToken);
      set({ cameras, isLoading: false, loaded: true });
    } catch (err) {
      const { status, message } = describeRequestError(err);
      console.error("[camera-catalogue] read failed:", status ?? "(no status)", message);
      set({
        error: err instanceof Error ? err.message : "Failed to fetch cameras",
        isLoading: false
      });
    }
  },
  getCameraById: (id) => id == null ? void 0 : get().cameras.find((c) => c.id === id),
  clear: () => set({ cameras: [], error: null, loaded: false })
}));

// src/stores/ManufacturerCatalogStore.ts
import { create as create6 } from "zustand";
import { getManufacturerCatalog } from "@repo/front-end-services";
var EMPTY = /* @__PURE__ */ new Map();
var inFlight2 = null;
var generation = 0;
var useManufacturerCatalogStore = create6((set, get) => ({
  status: "idle",
  rows: [],
  byReference: EMPTY,
  load: (apiBaseUrl, getAccessToken) => {
    if (inFlight2) return inFlight2;
    const { status, rows: held } = get();
    if (status === "ready" && held.length > 0) return Promise.resolve();
    const mine = generation;
    set({ status: "loading" });
    const self = {};
    const read = (async () => {
      await Promise.resolve();
      try {
        const token = await getAccessToken();
        const rows = await getManufacturerCatalog(apiBaseUrl, token);
        if (mine !== generation) return;
        set({
          status: "ready",
          rows,
          byReference: new Map(rows.map((r) => [r.entity_bid, r]))
        });
      } catch (e) {
        if (mine !== generation) return;
        console.error("Error loading the equipment library:", e);
        set({ status: "error", rows: [], byReference: EMPTY });
      } finally {
        if (inFlight2 === self.read) inFlight2 = null;
      }
    })();
    self.read = read;
    inFlight2 = read;
    return read;
  },
  reset: () => {
    generation += 1;
    inFlight2 = null;
    set({ status: "idle", rows: [], byReference: EMPTY });
  }
}));

// src/stores/DeveloperStore.ts
import { create as create7 } from "zustand";
import { subscribeWithSelector as subscribeWithSelector3 } from "zustand/middleware";
import { devtools as devtools3 } from "zustand/middleware";
var ConfigurationSource = /* @__PURE__ */ ((ConfigurationSource2) => {
  ConfigurationSource2["DEFAULT_BATTERSEA"] = "Default (Battersea)";
  ConfigurationSource2["ORGANIZATION"] = "Organization";
  ConfigurationSource2["TWIN_ADMIN"] = "Twin Admin (camera_start_args)";
  ConfigurationSource2["TWIN_PHYSICAL_MODEL"] = "Twin Physical Model";
  ConfigurationSource2["SITE_BOUNDARY_CENTER"] = "Site Boundary Center";
  ConfigurationSource2["USER_NAVIGATION"] = "User Navigation";
  ConfigurationSource2["MAP_STORE"] = "Map Store Update";
  ConfigurationSource2["SPECIAL_CASE"] = "Special Case (Hardcoded)";
  return ConfigurationSource2;
})(ConfigurationSource || {});
var useDeveloperStore = create7()(
  devtools3(
    subscribeWithSelector3((set, get) => ({
      // Initial State
      isEnabled: process.env.NODE_ENV === "development",
      currentCamera: null,
      currentMapOrigin: null,
      mapOriginHistory: [],
      cameraHistory: [],
      mapOriginSource: null,
      cameraSource: null,
      // Basic Actions
      setEnabled: (enabled) => {
        set({ isEnabled: enabled }, false, "setEnabled");
      },
      setMapOrigin: (mapOrigin, source, details) => {
        const { isEnabled, mapOriginHistory } = get();
        if (!isEnabled) return;
        const entry = {
          value: { ...mapOrigin },
          source,
          timestamp: /* @__PURE__ */ new Date(),
          details
        };
        set(
          {
            currentMapOrigin: mapOrigin,
            mapOriginSource: source,
            mapOriginHistory: [...mapOriginHistory, entry]
          },
          false,
          "setMapOrigin"
        );
      },
      setCamera: (camera, source, details) => {
        const { isEnabled, cameraHistory } = get();
        if (!isEnabled) return;
        const entry = {
          value: { ...camera },
          source,
          timestamp: /* @__PURE__ */ new Date(),
          details
        };
        set(
          {
            currentCamera: camera,
            cameraSource: source,
            cameraHistory: [...cameraHistory, entry]
          },
          false,
          "setCamera"
        );
      },
      clearHistory: () => {
        set(
          {
            mapOriginHistory: [],
            cameraHistory: []
          },
          false,
          "clearHistory"
        );
      },
      clearMapOriginHistory: () => {
        set({ mapOriginHistory: [] }, false, "clearMapOriginHistory");
      },
      clearCameraHistory: () => {
        set({ cameraHistory: [] }, false, "clearCameraHistory");
      },
      logConfiguration: (message, data) => {
        const { isEnabled } = get();
        if (!isEnabled) return;
      },
      getMapOriginSummary: () => {
        const { currentMapOrigin, mapOriginSource } = get();
        if (!currentMapOrigin || !mapOriginSource) {
          return "No map origin set";
        }
        return `${mapOriginSource}: lat=${currentMapOrigin.latitude.toFixed(6)}, lng=${currentMapOrigin.longitude.toFixed(6)}`;
      },
      getCameraSummary: () => {
        const { currentCamera, cameraSource } = get();
        if (!currentCamera || !cameraSource) {
          return "No camera position set";
        }
        const { lat, lng, zoom, bearing, pitch } = currentCamera;
        return `${cameraSource}: lat=${lat.toFixed(6)}, lng=${lng.toFixed(6)}, zoom=${zoom?.toFixed(1) || "N/A"}, bearing=${bearing?.toFixed(1) || "N/A"}\xB0, pitch=${pitch?.toFixed(1) || "N/A"}\xB0`;
      }
    })),
    {
      name: "developer-store"
      // Name for devtools
    }
  )
);
var useDeveloperEnabled = () => useDeveloperStore((state) => state.isEnabled);
var useCurrentCamera = () => useDeveloperStore((state) => state.currentCamera);
var useCurrentMapOrigin = () => useDeveloperStore((state) => state.currentMapOrigin);
var useCameraSource = () => useDeveloperStore((state) => state.cameraSource);
var useMapOriginSource = () => useDeveloperStore((state) => state.mapOriginSource);
var useCameraHistory = () => useDeveloperStore((state) => state.cameraHistory);
var useMapOriginHistory = () => useDeveloperStore((state) => state.mapOriginHistory);
var useMapOriginSummary = () => useDeveloperStore((state) => state.getMapOriginSummary());
var useCameraSummary = () => useDeveloperStore((state) => state.getCameraSummary());

// src/stores/NotificationStore.ts
import { create as create8 } from "zustand";
import { subscribeWithSelector as subscribeWithSelector4 } from "zustand/middleware";
import { devtools as devtools4 } from "zustand/middleware";
import {
  getNotifications,
  getNotificationsByOrganisation,
  getNotificationById,
  markNotificationAsRead,
  markNotificationAsUnread,
  markAllNotificationsAsRead,
  getLegalNotices,
  acceptTerms
} from "@repo/front-end-services";
var useNotificationStore = create8()(
  devtools4(
    subscribeWithSelector4((set, get) => ({
      // Initial State
      notifications: [],
      isLoading: false,
      error: null,
      lastFetched: null,
      selectedNotification: null,
      isLoadingDetail: false,
      orgBid: null,
      notificationsFetchSeq: 0,
      legalNotices: [],
      // Basic Actions
      setNotifications: (notifications) => {
        set(
          {
            notifications,
            lastFetched: /* @__PURE__ */ new Date(),
            error: null
          },
          false,
          "setNotifications"
        );
      },
      setLoading: (loading) => {
        set({ isLoading: loading }, false, "setLoading");
      },
      setError: (error) => {
        set({ error }, false, "setError");
      },
      clearError: () => {
        set({ error: null }, false, "clearError");
      },
      setSelectedNotification: (notification) => {
        set({ selectedNotification: notification }, false, "setSelectedNotification");
      },
      setLoadingDetail: (loading) => {
        set({ isLoadingDetail: loading }, false, "setLoadingDetail");
      },
      // Async Actions
      fetchNotifications: async (baseUrl, accessToken, twinId, isRead) => {
        const { setLoading, setError, setNotifications } = get();
        try {
          setLoading(true);
          setError(null);
          const data = await getNotifications(baseUrl, accessToken, twinId, isRead);
          setNotifications(data || []);
        } catch (error) {
          console.error("NotificationStore: Error fetching notifications:", error);
          setError(
            error instanceof Error ? error.message : String(error) || "Failed to fetch notifications"
          );
        } finally {
          setLoading(false);
        }
      },
      fetchOrgNotifications: async (baseUrl, accessToken, orgBid, isRead) => {
        const { setLoading, setError, setNotifications } = get();
        const seq = get().notificationsFetchSeq + 1;
        set({ orgBid, notificationsFetchSeq: seq }, false, "setOrgBid");
        try {
          setLoading(true);
          setError(null);
          const data = await getNotificationsByOrganisation(baseUrl, accessToken, orgBid, isRead);
          if (get().notificationsFetchSeq !== seq) {
            return;
          }
          setNotifications(data || []);
        } catch (error) {
          console.error("NotificationStore: Error fetching organisation notifications:", error);
          if (get().notificationsFetchSeq !== seq) {
            return;
          }
          setError(
            error instanceof Error ? error.message : String(error) || "Failed to fetch notifications"
          );
        } finally {
          if (get().notificationsFetchSeq === seq) {
            setLoading(false);
          }
        }
      },
      fetchNotificationById: async (baseUrl, accessToken, notificationId) => {
        const { setLoadingDetail, setError, setSelectedNotification } = get();
        try {
          setLoadingDetail(true);
          setError(null);
          const notification = await getNotificationById(baseUrl, accessToken, notificationId);
          if (notification) {
            setSelectedNotification(notification);
          }
        } catch (error) {
          console.error("Error fetching notification details:", error);
          setError(
            error instanceof Error ? error.message : String(error) || "Failed to fetch notification details"
          );
        } finally {
          setLoadingDetail(false);
        }
      },
      markAsRead: async (baseUrl, accessToken, notificationId, twinId) => {
        const { fetchNotifications, fetchOrgNotifications, setError } = get();
        try {
          setError(null);
          await markNotificationAsRead(baseUrl, accessToken, notificationId);
          const { orgBid } = get();
          if (orgBid) {
            await fetchOrgNotifications(baseUrl, accessToken, orgBid);
          } else {
            await fetchNotifications(baseUrl, accessToken, twinId);
          }
        } catch (error) {
          console.error("NotificationStore: Error marking notification as read:", error);
          setError(
            error instanceof Error ? error.message : String(error) || "Failed to mark notification as read"
          );
        }
      },
      markAsUnread: async (baseUrl, accessToken, notificationId, twinId) => {
        const { fetchNotifications, fetchOrgNotifications, setError } = get();
        try {
          setError(null);
          await markNotificationAsUnread(baseUrl, accessToken, notificationId);
          const { orgBid } = get();
          if (orgBid) {
            await fetchOrgNotifications(baseUrl, accessToken, orgBid);
          } else {
            await fetchNotifications(baseUrl, accessToken, twinId);
          }
        } catch (error) {
          console.error("NotificationStore: Error marking notification as unread:", error);
          setError(
            error instanceof Error ? error.message : String(error) || "Failed to mark notification as unread"
          );
        }
      },
      refreshNotifications: async (baseUrl, accessToken, twinId) => {
        const { fetchNotifications } = get();
        await fetchNotifications(baseUrl, accessToken, twinId);
      },
      markAllNotificationsAsRead: async (baseUrl, accessToken, orgBid) => {
        const { fetchOrgNotifications, setError } = get();
        const reconcileIfCurrent = async () => {
          if (get().orgBid === orgBid) {
            await fetchOrgNotifications(baseUrl, accessToken, orgBid);
          }
        };
        try {
          setError(null);
          const { marked } = await markAllNotificationsAsRead(baseUrl, accessToken, orgBid);
          await reconcileIfCurrent();
          return marked;
        } catch (error) {
          console.error("NotificationStore: Error marking all notifications as read:", error);
          setError(
            error instanceof Error ? error.message : String(error) || "Failed to mark all notifications as read"
          );
          await reconcileIfCurrent();
          return null;
        }
      },
      // Legal notices (FR6). getLegalNotices never throws (fail-open, NF1); on the off-chance it
      // does, we swallow it here too — a notices fetch must never disturb the product.
      fetchLegalNotices: async (baseUrl, accessToken) => {
        try {
          const legalNotices = await getLegalNotices(baseUrl, accessToken);
          set({ legalNotices }, false, "setLegalNotices");
        } catch (error) {
          console.error("NotificationStore: Error fetching legal notices:", error);
          set({ legalNotices: [] }, false, "setLegalNotices");
        }
      },
      acknowledgeLegalNotice: async (baseUrl, accessToken, notice) => {
        const { fetchLegalNotices } = get();
        try {
          const accepted = await acceptTerms(baseUrl, accessToken, [notice]);
          if (!accepted) {
            return false;
          }
          await fetchLegalNotices(baseUrl, accessToken);
          return true;
        } catch (error) {
          console.error("NotificationStore: Error acknowledging legal notice:", error);
          return false;
        }
      }
    })),
    {
      name: "notification-store"
      // Name for devtools
    }
  )
);
var useNotifications = () => useNotificationStore((state) => state.notifications);
var useLegalNotices = () => useNotificationStore((state) => state.legalNotices);
var useUnreadCount = () => useNotificationStore(
  (state) => state.notifications.filter((notification) => !notification.read_at).length + state.legalNotices.length
);
var useNotificationIsLoading = () => useNotificationStore((state) => state.isLoading);
var useNotificationError = () => useNotificationStore((state) => state.error);
var useSelectedNotification = () => useNotificationStore((state) => state.selectedNotification);
var useNotificationLoadingDetail = () => useNotificationStore((state) => state.isLoadingDetail);

// src/stores/OrganizationStore.ts
import { shallow as shallow2 } from "zustand/shallow";
import { createWithEqualityFn as createWithEqualityFn2 } from "zustand/traditional";
var buildStore2 = () => {
  const useStore = createWithEqualityFn2()((set, get) => {
    const storeData = {
      // Initial state
      auth0Organizations: [],
      localOrganizations: [],
      // Actions
      setAuth0Organizations: (auth0Organizations) => {
        set({ auth0Organizations });
      },
      addLocalOrganization: (organization) => {
        set((state) => ({
          localOrganizations: [...state.localOrganizations, organization]
        }));
      },
      clearOrganizations: () => {
        set({
          auth0Organizations: [],
          localOrganizations: []
        });
      },
      // Computed getter
      getAllOrganizations: () => {
        const { auth0Organizations, localOrganizations } = get();
        return [...auth0Organizations, ...localOrganizations];
      }
    };
    return storeData;
  }, shallow2);
  return useStore;
};
var useOrganizationStore = buildStore2();

// src/stores/SettingsStore.ts
import { shallow as shallow3 } from "zustand/shallow";
import { createWithEqualityFn as createWithEqualityFn3 } from "zustand/traditional";
var buildStore3 = () => {
  const useStore = createWithEqualityFn3()((set) => {
    const storeData = {
      settings: null,
      metrics: [],
      setSettings: (settings) => {
        set({ settings });
      },
      setMetrics: (metrics) => {
        set({ metrics });
      }
    };
    return storeData;
  }, shallow3);
  return useStore;
};
var useSettingsStore = buildStore3();

// src/stores/SpotlightStore.ts
import { shallow as shallow4 } from "zustand/shallow";
import { createWithEqualityFn as createWithEqualityFn4 } from "zustand/traditional";
var buildStore4 = () => {
  const useStore = createWithEqualityFn4()((set) => {
    const storeData = {
      activeSpotlightBid: null,
      spotlightFireCount: 0,
      fireSpotlight: (bid) => {
        const normalised = bid.trim().toUpperCase();
        if (!normalised) return;
        set((state) => ({
          activeSpotlightBid: normalised,
          spotlightFireCount: state.spotlightFireCount + 1
        }));
      },
      clearSpotlight: () => {
        set({ activeSpotlightBid: null });
      }
    };
    return storeData;
  }, shallow4);
  return useStore;
};
var useSpotlightStore = buildStore4();

// src/stores/TimelineCalendarSyncStore.ts
import { shallow as shallow5 } from "zustand/shallow";
import { createWithEqualityFn as createWithEqualityFn5 } from "zustand/traditional";
var buildStore5 = () => {
  const useStore = createWithEqualityFn5()((set) => {
    const storeData = {
      timelineDateTime: null,
      calendarDateTimeRange: null,
      setTimelineDateTime: (startDateTime) => {
        set({ timelineDateTime: startDateTime });
      },
      setCalendarDateRange: (startDateTime, finishDateTime) => {
        set({
          calendarDateTimeRange: {
            startDateTime,
            finishDateTime
          }
        });
      }
    };
    return storeData;
  }, shallow5);
  return useStore;
};
var useTimelineCalendarSyncStore = buildStore5();

// src/stores/TwinConfigStore.ts
import { shallow as shallow6 } from "zustand/shallow";
import { createWithEqualityFn as createWithEqualityFn6 } from "zustand/traditional";
var initialState2 = {
  twinConfig: null,
  preferences: null,
  heroMetrics: [],
  indicatorConfigs: [],
  leftFieldMessages: [],
  labels: void 0,
  isLoading: false,
  error: null
};
var buildStore6 = () => {
  const useStore = createWithEqualityFn6()((set, get) => {
    const storeData = {
      ...initialState2,
      setTwinConfig: (twinConfig) => {
        set({
          twinConfig,
          preferences: twinConfig?.preferences || null,
          heroMetrics: twinConfig?.hero_metrics || [],
          indicatorConfigs: twinConfig?.indicator_configs || [],
          leftFieldMessages: twinConfig?.left_field_messages || [],
          // Deliberately NOT `|| []` — see the field's doc comment. Absent must stay absent so
          // `Label3D` can tell "no answer" from "no rows".
          labels: twinConfig?.labels,
          error: null
        });
      },
      setPreferences: (preferences) => {
        set({ preferences });
      },
      setHeroMetrics: (heroMetrics) => {
        set({ heroMetrics });
      },
      setIndicatorConfigs: (indicatorConfigs) => {
        set({ indicatorConfigs });
      },
      setLeftFieldMessages: (leftFieldMessages) => {
        set({ leftFieldMessages });
      },
      setLoading: (isLoading) => {
        set({ isLoading });
      },
      setError: (error) => {
        set({ error });
      },
      clearTwinConfig: () => {
        set(initialState2);
      },
      hasPreferences: () => {
        return get().preferences !== null;
      },
      hasHeroMetrics: () => {
        return get().heroMetrics.length > 0;
      },
      hasIndicatorConfigs: () => {
        return get().indicatorConfigs.length > 0;
      },
      hasLeftFieldMessages: () => {
        return get().leftFieldMessages.length > 0;
      }
    };
    return storeData;
  }, shallow6);
  return useStore;
};
var useTwinConfigStore = buildStore6();

// src/stores/EquipmentClassificationStore.ts
import { shallow as shallow7 } from "zustand/shallow";
import { createWithEqualityFn as createWithEqualityFn7 } from "zustand/traditional";
var initialState3 = {
  classifications: [],
  loading: false,
  error: null,
  fetchedForTwinBid: null,
  retryNonce: 0
};
var buildStore7 = () => {
  const useStore = createWithEqualityFn7()((set, get) => {
    const storeData = {
      ...initialState3,
      setClassifications: (twinBid, classifications) => {
        set({ classifications, fetchedForTwinBid: twinBid, error: null });
      },
      setLoading: (loading) => {
        set({ loading });
      },
      setError: (error) => {
        set({ error });
      },
      clearEquipmentClassifications: () => {
        set({ classifications: [], loading: false, error: null, fetchedForTwinBid: null });
      },
      retryEquipmentClassifications: () => {
        set((state) => ({ error: null, fetchedForTwinBid: null, retryNonce: state.retryNonce + 1 }));
      },
      hasClassifications: () => {
        return get().classifications.length > 0;
      }
    };
    return storeData;
  }, shallow7);
  return useStore;
};
var useEquipmentClassificationStore = buildStore7();

// src/stores/TwinStore.ts
import { create as create9 } from "zustand";
import { subscribeWithSelector as subscribeWithSelector5 } from "zustand/middleware";
import { devtools as devtools5 } from "zustand/middleware";
var useTwinStore = create9()(
  devtools5(
    subscribeWithSelector5((set, get) => ({
      // Initial State
      availableTwins: [],
      selectedTwinBID: null,
      isLoading: false,
      lastUpdated: null,
      // Actions
      setAvailableTwins: (twins) => {
        set(
          {
            availableTwins: twins,
            lastUpdated: Date.now(),
            isLoading: false
          },
          false,
          "setAvailableTwins"
        );
      },
      setSelectedTwinBID: (bid) => {
        set({ selectedTwinBID: bid }, false, "setSelectedTwinBID");
      },
      setLoading: (loading) => {
        set({ isLoading: loading }, false, "setLoading");
      },
      clearTwins: () => {
        set(
          {
            availableTwins: [],
            selectedTwinBID: null,
            lastUpdated: null
          },
          false,
          "clearTwins"
        );
      },
      clearSelection: () => {
        set({ selectedTwinBID: null }, false, "clearSelection");
      },
      // Convenience Methods
      getTwinByBID: (bid) => {
        const { availableTwins } = get();
        return availableTwins.find((twin) => twin.bid === bid);
      },
      getTwinCount: () => {
        const { availableTwins } = get();
        return availableTwins.length;
      },
      flyToTwin: (bid) => {
        const { getTwinByBID, setSelectedTwinBID } = get();
        const twin = getTwinByBID(bid);
        if (twin) {
          setSelectedTwinBID(bid);
          return twin;
        }
        return null;
      }
    })),
    {
      name: "twin-store"
    }
  )
);
var useTwinMarkers = () => useTwinStore((state) => state.availableTwins);
var useSelectedTwinBID = () => useTwinStore((state) => state.selectedTwinBID);
var useTwinLoading = () => useTwinStore((state) => state.isLoading);
var useTwinCount = () => useTwinStore((state) => state.availableTwins.length);
var extractTwinMarkerData = (twins) => {
  return twins.map((twin) => ({
    id: twin.id,
    bid: twin.bid,
    name: twin.name,
    latitude: twin.latitude,
    longitude: twin.longitude
  }));
};

// src/stores/UiSettingsStore.ts
import { shallow as shallow8 } from "zustand/shallow";
import { createWithEqualityFn as createWithEqualityFn8 } from "zustand/traditional";
var DEFAULT_DIRECTIONAL_LIGHT_PROPS = {
  intensity: 0.78,
  dx: 3.38,
  dy: 4.48,
  dz: -2.13,
  // target
  x: 3.06,
  y: 4.41,
  z: 8.21
};
var DEFAULT_HEMI_LIGHT_PROPS = {
  intensity: 0.57,
  x: 6.98,
  y: 8.21,
  z: -22.52
  // calculate x-z to be horz centre of twin
};
var getDefaultViewerParams = (envString) => {
  const viewerParams = {
    showDeveloperTools: false,
    directedPlaceholderGeometry: false,
    hidePlaceholdersWhenModelsAreAvailable: true,
    showStats: false,
    altContainment: false,
    excludeCap: false,
    sceneHelpers: false
  };
  if (!envString) return viewerParams;
  try {
    const params = JSON.parse(envString);
    Object.keys(viewerParams).forEach((k) => {
      const val = params[k];
      if (val !== void 0) {
        viewerParams[k] = val;
      }
    });
  } catch (err) {
    console.error(`Error parsing viewer params (using default values): ${String(err)}`);
  }
  return viewerParams;
};
var defaultViewerParams = getDefaultViewerParams();
var buildStore8 = () => {
  const useStore = createWithEqualityFn8()((set, get) => {
    const storeData = {
      rotationY: 0,
      setRotationY: (rotationY) => set({ rotationY }),
      directedPlaceholderGeometry: defaultViewerParams.directedPlaceholderGeometry,
      setDirectedPlaceholderGeometry: (directedPlaceholderGeometry) => set({ directedPlaceholderGeometry }),
      hidePlaceholdersWhenModelsAreAvailable: defaultViewerParams.hidePlaceholdersWhenModelsAreAvailable,
      setHidePlaceholdersWhenModelsAreAvailable: (hidePlaceholdersWhenModelsAreAvailable) => set({ hidePlaceholdersWhenModelsAreAvailable }),
      analyzeScene: () => {
      },
      setAnalyzeScene: (analyzeScene) => set({ analyzeScene }),
      showDeveloperTools: defaultViewerParams.showDeveloperTools,
      setShowDeveloperTools: (showDeveloperTools) => set({ showDeveloperTools }),
      toggleDeveloperTools: () => set({
        showDeveloperTools: !get().showDeveloperTools
      }),
      showStats: defaultViewerParams.showStats,
      setShowStats: (showStats) => set({ showStats }),
      debugHierarchy: false,
      setDebugHierarchy: (debugHierarchy) => set({ debugHierarchy }),
      showSceneHelpers: defaultViewerParams.sceneHelpers,
      setShowSceneHelpers: (showSceneHelpers) => set({ showSceneHelpers }),
      showFloors: false,
      setShowFloors: (showFloors) => set({ showFloors }),
      excludeCap: defaultViewerParams.excludeCap,
      setExcludeCap: (excludeCap) => set({ excludeCap }),
      altContainment: defaultViewerParams.altContainment,
      setAltContainment: (altContainment) => set({ altContainment }),
      perContainerDelta: 0.25,
      setPerContainerDelta: (perContainerDelta) => set({ perContainerDelta }),
      directionalLightProps: DEFAULT_DIRECTIONAL_LIGHT_PROPS,
      setDirectionalLightProps: (directionalLightProps) => {
        const { directionalLightProps: origProps } = get();
        set({
          directionalLightProps: {
            ...origProps,
            ...directionalLightProps
          }
        });
      },
      hemiLightProps: DEFAULT_HEMI_LIGHT_PROPS,
      setHemiLightProps: (hemiLightProps) => {
        const { hemiLightProps: origProps } = get();
        set({
          hemiLightProps: {
            ...origProps,
            ...hemiLightProps
          }
        });
      }
    };
    return storeData;
  }, shallow8);
  return useStore;
};
var useUiSettingsStore = buildStore8();
var initUiSettingsFromEnv = (envString) => {
  if (!envString) return;
  const params = getDefaultViewerParams(envString);
  useUiSettingsStore.setState({
    showDeveloperTools: params.showDeveloperTools,
    directedPlaceholderGeometry: params.directedPlaceholderGeometry,
    hidePlaceholdersWhenModelsAreAvailable: params.hidePlaceholdersWhenModelsAreAvailable,
    showStats: params.showStats,
    altContainment: params.altContainment,
    excludeCap: params.excludeCap,
    showSceneHelpers: params.sceneHelpers
  });
};

// src/stores/UserStore.ts
import { shallow as shallow9 } from "zustand/shallow";
import { createWithEqualityFn as createWithEqualityFn9 } from "zustand/traditional";
var STORAGE_KEY_ORG_BID = "twinlabs_selected_org_bid";
var STORAGE_KEY_ORG_NAME = "twinlabs_selected_org_name";
var STORAGE_KEY_ORG_ID = "twinlabs_selected_org_id";
var STORAGE_KEY_ORG_LAT = "twinlabs_selected_org_lat";
var STORAGE_KEY_ORG_LNG = "twinlabs_selected_org_lng";
var STORAGE_KEY_LEGAL = "twinlabs_legal_acceptance";
var isValidOrgBID = (value) => {
  return typeof value === "string" && value.trim().length > 0;
};
var parseValidOrgID = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }
  const parsed = parseInt(value, 10);
  if (isNaN(parsed) || !isFinite(parsed) || parsed <= 0 || parsed !== Math.floor(parsed)) {
    return null;
  }
  return parsed;
};
var parseValidOrgName = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};
var persistToLocalStorage = (authMetadata) => {
  try {
    if (authMetadata?.orgBID) {
      localStorage.setItem(STORAGE_KEY_ORG_BID, authMetadata.orgBID);
      if (authMetadata.organisation) {
        localStorage.setItem(STORAGE_KEY_ORG_NAME, authMetadata.organisation);
      }
      if (authMetadata.orgID) {
        localStorage.setItem(STORAGE_KEY_ORG_ID, String(authMetadata.orgID));
      }
      if (authMetadata.orgLatitude) {
        localStorage.setItem(STORAGE_KEY_ORG_LAT, authMetadata.orgLatitude);
      }
      if (authMetadata.orgLongitude) {
        localStorage.setItem(STORAGE_KEY_ORG_LNG, authMetadata.orgLongitude);
      }
    } else {
      localStorage.removeItem(STORAGE_KEY_ORG_BID);
      localStorage.removeItem(STORAGE_KEY_ORG_NAME);
      localStorage.removeItem(STORAGE_KEY_ORG_ID);
      localStorage.removeItem(STORAGE_KEY_ORG_LAT);
      localStorage.removeItem(STORAGE_KEY_ORG_LNG);
    }
    if (authMetadata?.legal) {
      localStorage.setItem(STORAGE_KEY_LEGAL, JSON.stringify(authMetadata.legal));
    } else {
      localStorage.removeItem(STORAGE_KEY_LEGAL);
    }
  } catch (error) {
    console.error("Error saving data to localStorage:", error);
  }
};
var buildStore9 = () => {
  const useStore = createWithEqualityFn9()((set, get) => {
    const storeData = {
      authMetadata: null,
      setAuthMetadata: (authMetadata) => {
        const prev = get().authMetadata;
        const newAuthMetadata = {
          ...prev,
          ...authMetadata,
          tokens: {
            ...prev?.tokens,
            ...authMetadata.tokens
          }
        };
        persistToLocalStorage(newAuthMetadata);
        set({ authMetadata: newAuthMetadata });
      }
    };
    return storeData;
  }, shallow9);
  return useStore;
};
var useUserStore = buildStore9();
var initUserStoreFromStorage = () => {
  const currentAuth = useUserStore.getState().authMetadata;
  if (currentAuth?.orgBID) return;
  try {
    if (typeof localStorage === "undefined") return;
    const hasAuth0Session = Object.keys(localStorage).some((key) => key.startsWith("@@auth0spajs@@"));
    if (!hasAuth0Session) {
      localStorage.removeItem(STORAGE_KEY_ORG_BID);
      localStorage.removeItem(STORAGE_KEY_ORG_NAME);
      localStorage.removeItem(STORAGE_KEY_ORG_ID);
      localStorage.removeItem(STORAGE_KEY_ORG_LAT);
      localStorage.removeItem(STORAGE_KEY_ORG_LNG);
      localStorage.removeItem(STORAGE_KEY_LEGAL);
      return;
    }
    const savedOrgBID = localStorage.getItem(STORAGE_KEY_ORG_BID);
    const savedOrgName = localStorage.getItem(STORAGE_KEY_ORG_NAME);
    const savedOrgID = localStorage.getItem(STORAGE_KEY_ORG_ID);
    const savedOrgLat = localStorage.getItem(STORAGE_KEY_ORG_LAT);
    const savedOrgLng = localStorage.getItem(STORAGE_KEY_ORG_LNG);
    const savedLegal = localStorage.getItem(STORAGE_KEY_LEGAL);
    const validatedOrgBID = isValidOrgBID(savedOrgBID) ? savedOrgBID.trim() : null;
    const validatedOrgID = parseValidOrgID(savedOrgID);
    const validatedOrgName = parseValidOrgName(savedOrgName);
    let validatedLegal = void 0;
    if (savedLegal) {
      try {
        validatedLegal = JSON.parse(savedLegal);
      } catch (error) {
        console.warn("Invalid legal data found in localStorage, clearing:", error);
        localStorage.removeItem(STORAGE_KEY_LEGAL);
      }
    }
    if (validatedOrgBID) {
      useUserStore.setState({
        authMetadata: {
          ...currentAuth,
          orgBID: validatedOrgBID,
          organisation: validatedOrgName || void 0,
          orgID: validatedOrgID || void 0,
          orgLatitude: savedOrgLat || void 0,
          orgLongitude: savedOrgLng || void 0,
          legal: validatedLegal || currentAuth?.legal,
          tokens: currentAuth?.tokens
        }
      });
    } else if (savedOrgBID || savedOrgName || savedOrgID) {
      console.warn("Invalid organization data found in localStorage, clearing corrupted values");
      localStorage.removeItem(STORAGE_KEY_ORG_BID);
      localStorage.removeItem(STORAGE_KEY_ORG_NAME);
      localStorage.removeItem(STORAGE_KEY_ORG_ID);
      localStorage.removeItem(STORAGE_KEY_ORG_LAT);
      localStorage.removeItem(STORAGE_KEY_ORG_LNG);
    }
    if (validatedLegal && !currentAuth?.legal) {
      useUserStore.setState({
        authMetadata: {
          ...useUserStore.getState().authMetadata,
          legal: validatedLegal
        }
      });
    }
  } catch (error) {
    console.error("Error restoring org data from localStorage:", error);
    try {
      localStorage.removeItem(STORAGE_KEY_ORG_BID);
      localStorage.removeItem(STORAGE_KEY_ORG_NAME);
      localStorage.removeItem(STORAGE_KEY_ORG_ID);
      localStorage.removeItem(STORAGE_KEY_ORG_LAT);
      localStorage.removeItem(STORAGE_KEY_ORG_LNG);
    } catch (clearError) {
      console.error("Error clearing corrupted localStorage data:", clearError);
    }
  }
};

// src/stores/MariaConfigStore.ts
import { shallow as shallow10 } from "zustand/shallow";
import { createWithEqualityFn as createWithEqualityFn10 } from "zustand/traditional";
var useMariaConfigStore = createWithEqualityFn10()(
  (set) => ({
    aiUrl: "",
    setAiUrl: (aiUrl) => set({ aiUrl })
  }),
  shallow10
);
var initMariaConfigFromEnv = (middlewareApi) => {
  if (!middlewareApi) {
    console.warn("[MariaConfigStore] middlewareApi is empty \u2014 Maria AI modal will not work.");
    return;
  }
  useMariaConfigStore.getState().setAiUrl(`${middlewareApi}api/chat`);
};

// src/stores/TwinModelStore.ts
import { createRef } from "react";
import { shallow as shallow11 } from "zustand/shallow";
import { createWithEqualityFn as createWithEqualityFn11 } from "zustand/traditional";
import { twinFloorShape } from "@repo/front-end-utils";
var STORAGE_KEY_TWIN_BID = "twinlabs_selected_twin_bid";
var buildStore10 = () => {
  let initialTwinBId = null;
  try {
    if (typeof localStorage !== "undefined") {
      initialTwinBId = localStorage.getItem(STORAGE_KEY_TWIN_BID);
    }
  } catch (error) {
    console.error("Error restoring twinBId from localStorage:", error);
  }
  const useStore = createWithEqualityFn11()(
    (set, get) => ({
      lastTwin: null,
      twin: null,
      twinBId: initialTwinBId,
      multifloor: false,
      hasFloorLevel: false,
      fmlImportFile: { file: null, status: "unset" },
      mapRef: createRef(),
      sceneRef: createRef(),
      accessibleTwins: null,
      prefetchedTwinFlightBid: null,
      twinNeedsRefresh: false,
      twinDependenciesSatisfied: void 0,
      setTwin: (twin, multifloor, hasFloorLevel) => {
        const derived = twinFloorShape(twin?.physicalModel);
        set({
          lastTwin: get().twin,
          twin,
          multifloor: multifloor ?? derived.multifloor,
          hasFloorLevel: hasFloorLevel ?? multifloor ?? derived.hasFloorLevel
        });
      },
      setFmlImportFile: (fmlImportFile) => {
        set({ fmlImportFile });
      },
      setTwinBId: (twinBId) => {
        set({ twinBId, prefetchedTwinFlightBid: null });
        try {
          if (twinBId) {
            localStorage.setItem(STORAGE_KEY_TWIN_BID, twinBId);
          } else {
            localStorage.removeItem(STORAGE_KEY_TWIN_BID);
          }
        } catch (error) {
          console.error("Error saving twinBId to localStorage:", error);
        }
      },
      setTwinNeedsRefresh: (twinNeedsRefresh) => {
        set({ lastTwin: get().twin, twinNeedsRefresh });
      },
      setTwinDependenciesSatisfied: (twinDependenciesSatisfied) => {
        set({ twinDependenciesSatisfied });
      },
      setAccessibleTwins: (twins) => {
        set({ accessibleTwins: twins });
      },
      setPrefetchedTwinFlightBid: (bid) => {
        set({ prefetchedTwinFlightBid: bid });
      }
    }),
    shallow11
  );
  return useStore;
};
var useTwinModelStore = buildStore10();
var useTwinContext = useTwinModelStore;

// src/contexts/NotificationContext.tsx
import React, { createContext, useContext, useState, useCallback } from "react";
import { MessageBar } from "@fluentui/react-components";
import styled from "styled-components";
import { jsx, jsxs } from "react/jsx-runtime";
var NotificationContext = createContext(null);
var useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotificationContext must be used within a NotificationContextProvider");
  }
  return context;
};
var NotificationContainer = styled.div`
  position: fixed;
  top: 60px; /* Below the main nav */
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 0 1rem;
  pointer-events: none;

  .notification-item {
    pointer-events: auto;
    margin-bottom: 0.5rem;
    animation: slideDown 0.3s ease-out;
  }

  @keyframes slideDown {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;
var NotificationContextProvider = ({
  children
}) => {
  const [notifications, setNotifications] = useState([]);
  const showNotification = useCallback(
    (message, intent, duration = 5e3) => {
      const id = Date.now().toString();
      const notification = {
        id,
        message,
        intent,
        duration
      };
      setNotifications((prev) => [...prev, notification]);
      setTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      }, duration);
    },
    []
  );
  const showDgraphError = useCallback(
    (orgBid) => {
      showNotification(
        `Organisation ${orgBid} has no DGraph namespace configured. Some features may not be available.`,
        "warning",
        8e3
        // Show for 8 seconds since it's important
      );
    },
    [showNotification]
  );
  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);
  const contextValue = {
    showNotification,
    showDgraphError
  };
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.__showDgraphError = showDgraphError;
    }
    return () => {
      if (typeof window !== "undefined") {
        delete window.__showDgraphError;
      }
    };
  }, [showDgraphError]);
  return /* @__PURE__ */ jsxs(NotificationContext.Provider, { value: contextValue, children: [
    children,
    notifications.length > 0 && /* @__PURE__ */ jsx(NotificationContainer, { children: notifications.map((notification) => /* @__PURE__ */ jsx("div", { className: "notification-item", children: /* @__PURE__ */ jsxs(MessageBar, { intent: notification.intent, layout: "multiline", children: [
      notification.message,
      /* @__PURE__ */ jsx(
        "button",
        {
          onClick: () => removeNotification(notification.id),
          style: {
            background: "none",
            border: "none",
            cursor: "pointer",
            marginLeft: "8px",
            padding: "2px 6px",
            borderRadius: "2px",
            fontSize: "12px"
          },
          children: "\xD7"
        }
      )
    ] }) }, notification.id)) })
  ] });
};

// src/contexts/DialogContext.tsx
import {
  createContext as createContext2,
  useCallback as useCallback2,
  useContext as useContext2,
  useEffect,
  useMemo,
  useRef,
  useState as useState2
} from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var initialState4 = {
  showDialog: () => {
  },
  hideDialog: () => {
  },
  setDialogWidth: () => {
  },
  dialogProps: null
};
var DialogContext = createContext2(initialState4);
var useDialogContext = () => {
  return useContext2(DialogContext);
};
var DialogContextProvider = ({
  children,
  resetKey
}) => {
  const [dialogProps, setDialogProps] = useState2(null);
  const resetKeyRef = useRef(resetKey);
  useEffect(() => {
    if (resetKeyRef.current === resetKey) return;
    resetKeyRef.current = resetKey;
    setDialogProps(null);
  }, [resetKey]);
  const showDialog = useCallback2((props) => {
    setDialogProps(props);
  }, []);
  const hideDialog = useCallback2(() => {
    setDialogProps(null);
  }, []);
  const setDialogWidth = useCallback2((width) => {
    setDialogProps((prev) => prev ? { ...prev, width } : null);
  }, []);
  const contextValue = useMemo(() => {
    return {
      showDialog,
      hideDialog,
      setDialogWidth,
      dialogProps
    };
  }, [showDialog, hideDialog, setDialogWidth, dialogProps]);
  return /* @__PURE__ */ jsx2(DialogContext.Provider, { value: contextValue, children });
};

// src/contexts/FilterAndModeContexts.tsx
import {
  AppMode,
  FilterType,
  Roles,
  TimeUnit
} from "@repo/frontend-types";
import { createContext as createContext3, useCallback as useCallback3, useContext as useContext3, useEffect as useEffect2, useMemo as useMemo2, useState as useState3 } from "react";
import { DateTime } from "luxon";
import {
  buildHeroMetric,
  findIndicatorConfigForMetric,
  findMinMaxDates,
  getSelectionProfile,
  indicatorScopeFor,
  isDatelessCalendarMode,
  isHeroMetricEnabledForMode
} from "@repo/front-end-utils";
import { jsx as jsx3 } from "react/jsx-runtime";
var emptyClassificationFilters = () => ({
  equipmentType: [],
  manufacturer: [],
  floor: []
});
var initialFilterValue = {
  heroMetric: null,
  lastUpdatedAt: "",
  filter: [],
  startDateTime: null,
  finishDateTime: null,
  timeWindows: null,
  autoSelectCalendar: false,
  historicalReset: false,
  setLastUpdatedAt: () => {
  },
  setHeroMetric: () => {
  },
  setStartDateTime: () => {
  },
  setFinishDateTime: () => {
  },
  setTimeWindows: () => {
  },
  setAutoSelectCalendar: () => {
  },
  setHistoricalReset: () => {
  },
  setFilter: () => {
  },
  addFilter: () => {
  },
  removeFilter: () => {
  },
  removeFilterById: () => {
  },
  removeFilterByType: () => {
  },
  clearFilter: (dashboard) => {
  },
  toggleEquipmentSelection: () => {
  },
  commitSelection: () => {
  },
  resetSelectionToDefault: () => {
  },
  weekdayWindows: null,
  setWeekdayWindows: () => {
  },
  commitWeekdaySelection: () => {
  },
  resetWeekdaySelection: () => {
  },
  peakHoursActive: false,
  setPeakHoursActive: () => {
  },
  classificationFilters: emptyClassificationFilters(),
  toggleClassificationFilter: () => {
  },
  setClassificationFilter: () => {
  },
  clearClassificationFilters: () => {
  }
};
var initialModeValue = {
  appMode: AppMode.TWIN_SELECTION,
  calendarView: TimeUnit.WEEK,
  setAppModeWithFilterReset: (mode) => {
  },
  setCurrentCalendarView: (timeUnit) => {
  }
};
var ModeContext = createContext3(initialModeValue);
var FilterContext = createContext3(initialFilterValue);
var useFilterContext = () => {
  return useContext3(FilterContext);
};
var useModeContext = () => {
  return useContext3(ModeContext);
};
var FilterAndModeContextsProvider = (props) => {
  const { settings } = useSettingsStore();
  const { authMetadata } = useUserStore();
  const { heroMetrics, preferences, indicatorConfigs } = useTwinConfigStore();
  const twinBId = useTwinModelStore((state) => state.twinBId);
  const [filterState, setFilterState] = useState3(initialFilterValue);
  const getInitialAppMode = useCallback3(() => {
    if (!authMetadata?.roles) {
      return AppMode.TWIN_SELECTION;
    }
    const isTlAdmin = authMetadata.roles.includes(Roles.TL_ADMIN);
    const isPartner = authMetadata.roles.includes(Roles.PARTNER_USER);
    return isTlAdmin || isPartner ? AppMode.ORG_SELECTION : AppMode.TWIN_SELECTION;
  }, [authMetadata?.roles]);
  const [appMode, setAppMode] = useState3(getInitialAppMode());
  const [calendarView, setCalendarView] = useState3(TimeUnit.WEEK);
  const [hasInitialized, setHasInitialized] = useState3(false);
  useEffect2(() => {
    if (!hasInitialized && authMetadata?.roles) {
      const newMode = getInitialAppMode();
      if (newMode !== appMode) {
        setAppMode(newMode);
      }
      setHasInitialized(true);
    }
  }, [authMetadata?.roles, getInitialAppMode, appMode, hasInitialized]);
  const getIndicatorConfigForMetric = useCallback3(
    (indicatorMetricName, modes) => findIndicatorConfigForMetric(
      indicatorConfigs,
      indicatorMetricName,
      indicatorScopeFor(modes, appMode)
    ),
    [indicatorConfigs, appMode]
  );
  const currentHeroMetric = useMemo2(() => {
    if (!heroMetrics || heroMetrics.length === 0) {
      return null;
    }
    const enabledMetrics = heroMetrics.filter(
      (metric2) => isHeroMetricEnabledForMode(metric2.modes, appMode)
    );
    if (enabledMetrics.length === 0) {
      return null;
    }
    const sortedMetrics = [...enabledMetrics].sort(
      (a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name)
    );
    const metric = sortedMetrics[0];
    return buildHeroMetric(
      metric,
      getIndicatorConfigForMetric(metric.indicator_metric.name, metric.modes)
    );
  }, [heroMetrics, appMode, getIndicatorConfigForMetric, indicatorConfigs]);
  useEffect2(() => {
    setFilterState((prevState) => {
      const selected = prevState.heroMetric;
      const selectedStore = selected ? heroMetrics?.find((m) => m.id === selected.id) : void 0;
      const selectionValid = !!selectedStore && isHeroMetricEnabledForMode(selectedStore.modes, appMode);
      if (selectionValid) {
        const nextConfig = getIndicatorConfigForMetric(
          selectedStore.indicator_metric.name,
          selectedStore.modes
        );
        if (nextConfig === selected.indicatorConfig) {
          return prevState;
        }
        return { ...prevState, heroMetric: { ...selected, indicatorConfig: nextConfig } };
      }
      if (prevState.heroMetric === currentHeroMetric) {
        return prevState;
      }
      return { ...prevState, heroMetric: currentHeroMetric };
    });
  }, [appMode, heroMetrics, currentHeroMetric, getIndicatorConfigForMetric]);
  const setLastUpdatedAt = useCallback3((lastUpdatedAt) => {
    setFilterState((prevState) => ({
      ...prevState,
      lastUpdatedAt
    }));
  }, []);
  const removeFilterByType = useCallback3((type) => {
    setFilterState((prevState) => ({
      ...prevState,
      filter: prevState.filter.filter((item) => item.type !== type)
    }));
  }, []);
  const setStartDateTime = useCallback3((startDateTime) => {
    setFilterState((prevState) => ({
      ...prevState,
      startDateTime
    }));
  }, []);
  const setFinishDateTime = useCallback3((finishDateTime) => {
    setFilterState((prevState) => ({
      ...prevState,
      finishDateTime
    }));
  }, []);
  const setTimeWindows = useCallback3((timeWindows) => {
    setFilterState((prevState) => ({
      ...prevState,
      timeWindows
    }));
  }, []);
  const setWeekdayWindows = useCallback3((weekdayWindows) => {
    setFilterState((prevState) => ({
      ...prevState,
      weekdayWindows
    }));
  }, []);
  const setPeakHoursActive = useCallback3((peakHoursActive) => {
    setFilterState((prevState) => ({
      ...prevState,
      peakHoursActive
    }));
  }, []);
  const setAutoSelectCalendar = useCallback3((autoSelectCalendar) => {
    setFilterState((prevState) => ({
      ...prevState,
      autoSelectCalendar
    }));
  }, []);
  const toggleClassificationFilter = useCallback3(
    (hierarchy, node) => {
      setFilterState((prevState) => {
        const current = prevState.classificationFilters[hierarchy];
        const exists = current.some((n) => n.bid === node.bid);
        const next = exists ? current.filter((n) => n.bid !== node.bid) : [...current, { bid: node.bid, label: node.label }];
        return {
          ...prevState,
          classificationFilters: { ...prevState.classificationFilters, [hierarchy]: next }
        };
      });
    },
    []
  );
  const setClassificationFilter = useCallback3(
    (hierarchy, nodes) => {
      setFilterState((prevState) => ({
        ...prevState,
        classificationFilters: {
          ...prevState.classificationFilters,
          [hierarchy]: nodes.map((node) => ({ bid: node.bid, label: node.label }))
        }
      }));
    },
    []
  );
  const clearClassificationFilters = useCallback3(() => {
    setFilterState((prevState) => ({
      ...prevState,
      classificationFilters: emptyClassificationFilters()
    }));
  }, []);
  const applyDefaultSelectionForMode = useCallback3(
    (mode) => {
      const profile = getSelectionProfile(mode);
      if (!profile) {
        return;
      }
      const timeZone = settings?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const defaultSelection = profile.buildDefaultWindows({
        earliestOpeningTime: preferences?.earliestOpeningTime,
        latestClosingTime: preferences?.latestClosingTime,
        referenceDate: DateTime.now().setZone(timeZone)
      });
      if (defaultSelection) {
        setStartDateTime(defaultSelection.startDateTime);
        setFinishDateTime(defaultSelection.finishDateTime);
        setTimeWindows(defaultSelection.windows);
        setAutoSelectCalendar(true);
      } else {
        setAutoSelectCalendar(true);
        setTimeWindows(null);
        setStartDateTime(null);
        setFinishDateTime(null);
        removeFilterByType(FilterType.DATE);
        removeFilterByType(FilterType.TIME);
      }
    },
    [
      settings?.timeZone,
      preferences,
      setStartDateTime,
      setFinishDateTime,
      setTimeWindows,
      setAutoSelectCalendar,
      removeFilterByType
    ]
  );
  const setDefaultTimeRange = useCallback3(
    () => applyDefaultSelectionForMode(AppMode.HISTORICAL),
    [applyDefaultSelectionForMode]
  );
  const setHistoricalReset = useCallback3(
    (historicalReset) => {
      setFilterState((prevState) => ({
        ...prevState,
        historicalReset
      }));
      setTimeWindows(null);
      removeFilterByType(FilterType.DATE);
      removeFilterByType(FilterType.TIME);
      setDefaultTimeRange();
    },
    [removeFilterByType, setDefaultTimeRange, setTimeWindows]
  );
  const setHeroMetric = useCallback3((heroMetric) => {
    setFilterState((prevState) => ({
      ...prevState,
      heroMetric
    }));
  }, []);
  const setCurrentCalendarView = useCallback3(
    (timeUnit) => {
      setCalendarView(timeUnit);
    },
    [setCalendarView]
  );
  const setAppModeWithFilterReset = useCallback3(
    (mode) => {
      switch (mode) {
        case AppMode.HISTORICAL:
          if (filterState.timeWindows === null || filterState.timeWindows.length === 0) {
            setDefaultTimeRange();
          }
          break;
        case AppMode.LIVE:
        case AppMode.OPERATIONS:
          applyDefaultSelectionForMode(AppMode.LIVE);
          break;
        case AppMode.INSIGHTS:
          setTimeWindows(null);
          setStartDateTime(null);
          setFinishDateTime(null);
          setWeekdayWindows(null);
          setPeakHoursActive(false);
          break;
        case AppMode.EQUIPMENT:
          setTimeWindows(null);
          setStartDateTime(null);
          setFinishDateTime(null);
          setWeekdayWindows(null);
          setPeakHoursActive(false);
          removeFilterByType(FilterType.ASSET);
          clearClassificationFilters();
          break;
        case AppMode.ORG_SELECTION:
        case AppMode.TWIN_SELECTION:
        case AppMode.EDIT:
        case AppMode.DASHBOARD_UTILISATION:
          break;
        default: {
          const _exhaustive = mode;
        }
      }
      setAppMode(mode);
    },
    [
      setDefaultTimeRange,
      applyDefaultSelectionForMode,
      filterState.timeWindows,
      setAppMode,
      setWeekdayWindows,
      setPeakHoursActive,
      setTimeWindows,
      setStartDateTime,
      setFinishDateTime,
      removeFilterByType,
      clearClassificationFilters
    ]
  );
  const commitSelection = useCallback3(
    (timeWindows, mode = AppMode.HISTORICAL) => {
      if (filterState.historicalReset || filterState.autoSelectCalendar) return;
      if (timeWindows.length === 0) return;
      setAutoSelectCalendar(false);
      setAppModeWithFilterReset(mode);
      setTimeWindows(timeWindows);
    },
    [
      filterState.historicalReset,
      filterState.autoSelectCalendar,
      setAutoSelectCalendar,
      setAppModeWithFilterReset,
      setTimeWindows
    ]
  );
  const resetSelectionToDefault = useCallback3(() => {
    setHistoricalReset(true);
  }, [setHistoricalReset]);
  const commitWeekdaySelection = useCallback3(
    (weekdayWindows) => {
      if (weekdayWindows.length === 0) return;
      setWeekdayWindows(weekdayWindows);
    },
    [setWeekdayWindows]
  );
  const resetWeekdaySelection = useCallback3(() => {
    setWeekdayWindows(null);
  }, [setWeekdayWindows]);
  const addFilter = useCallback3((newFilter) => {
    setFilterState((prevState) => ({
      ...prevState,
      filter: [...prevState.filter, newFilter]
    }));
  }, []);
  const setFilter = useCallback3((newFilter) => {
    setFilterState((prevState) => {
      const dateFilter = prevState.filter.filter((item) => item.type === FilterType.DATE);
      const timeFilter = prevState.filter.filter((item) => item.type === FilterType.TIME);
      return {
        ...prevState,
        filter: dateFilter.concat(timeFilter).concat(newFilter)
      };
    });
  }, []);
  const removeFilterById = useCallback3(
    (id) => {
      const updatedFilter = filterState.filter.filter((filter) => filter.id !== id);
      setFilterState((prevState) => ({
        ...prevState,
        filter: updatedFilter
      }));
    },
    [filterState.filter]
  );
  const removeFilter = useCallback3(
    (index) => {
      const filterToRemove = filterState.filter[index];
      if (!filterToRemove) {
        console.error("No filter found at index:", index);
        return;
      }
      let updatedFilter = filterState.filter.filter((_, i) => i !== index);
      if (filterToRemove.type === FilterType.ENTITY) {
        updatedFilter = updatedFilter.filter((filter, i) => {
          return !(filter.type === FilterType.ENTITY && i >= index);
        });
      }
      if (isDatelessCalendarMode(appMode)) {
        if (filterToRemove.type === FilterType.EQUIPMENT_TYPE || filterToRemove.type === FilterType.MANUFACTURER || filterToRemove.type === FilterType.FLOOR) {
          if (filterToRemove.bId) {
            const hierarchy = filterToRemove.type === FilterType.EQUIPMENT_TYPE ? "equipmentType" : filterToRemove.type === FilterType.MANUFACTURER ? "manufacturer" : "floor";
            toggleClassificationFilter(hierarchy, {
              bid: filterToRemove.bId,
              label: filterToRemove.label
            });
          }
          return;
        }
        if (filterToRemove.type === FilterType.PEAK) {
          setPeakHoursActive(false);
          setWeekdayWindows(null);
        } else if (filterToRemove.type === FilterType.DATE) {
          setWeekdayWindows(null);
        } else if (filterToRemove.type === FilterType.TIME) {
          const weekdays = Array.from(
            new Set((filterState.weekdayWindows ?? []).map((window2) => window2.weekday))
          );
          setWeekdayWindows(
            weekdays.length > 0 ? weekdays.map((weekday) => ({ weekday, start_hour: 0, end_hour: 24 })) : null
          );
        } else {
          setFilterState((prevAppState) => ({
            ...prevAppState,
            filter: updatedFilter
          }));
        }
        return;
      }
      if (filterToRemove.type === FilterType.DATE) {
        updatedFilter = updatedFilter.filter((item) => item.type !== FilterType.TIME);
      }
      if (filterToRemove.type === FilterType.TIME) {
        if (filterState.timeWindows && filterState.timeWindows.length > 0) {
          const uniqueDates = /* @__PURE__ */ new Set();
          filterState.timeWindows.forEach(([start, end]) => {
            const startDate = DateTime.fromISO(start).toISODate();
            const endDate = DateTime.fromISO(end).toISODate();
            if (startDate) uniqueDates.add(startDate);
            if (endDate && endDate !== startDate) uniqueDates.add(endDate);
          });
          const expandedTimeWindows = Array.from(uniqueDates).map((date) => {
            const start = `${date}T00:00:00`;
            const end = `${date}T23:59:59`;
            return [start, end];
          });
          setAutoSelectCalendar(true);
          setTimeWindows(expandedTimeWindows);
        } else if (filterState.startDateTime && filterState.finishDateTime) {
          const startDate = DateTime.fromISO(filterState.startDateTime);
          const endDate = DateTime.fromISO(filterState.finishDateTime);
          const timeWindows = [];
          let currentDate = startDate.startOf("day");
          while (currentDate <= endDate.endOf("day")) {
            const dateStr = currentDate.toISODate();
            if (dateStr) {
              timeWindows.push([`${dateStr}T00:00:00`, `${dateStr}T23:59:59`]);
            }
            currentDate = currentDate.plus({ days: 1 });
          }
          setAutoSelectCalendar(true);
          setTimeWindows(timeWindows);
        }
        updatedFilter = updatedFilter.filter((item) => item.type !== FilterType.TIME);
      }
      if (filterToRemove.type === FilterType.DATE) {
        setAutoSelectCalendar(true);
        setTimeWindows(null);
        setAppMode(AppMode.LIVE);
      }
      setFilterState((prevAppState) => ({
        ...prevAppState,
        filter: updatedFilter
      }));
    },
    [
      filterState.filter,
      filterState.finishDateTime,
      filterState.startDateTime,
      filterState.timeWindows,
      filterState.weekdayWindows,
      appMode,
      setTimeWindows,
      setWeekdayWindows,
      setPeakHoursActive,
      setAutoSelectCalendar,
      setAppMode,
      toggleClassificationFilter
    ]
  );
  const clearFilter = useCallback3(
    (dashboard = false) => {
      if (isDatelessCalendarMode(appMode)) {
        setFilterState((prevState) => ({ ...prevState, filter: [] }));
        setWeekdayWindows(null);
        setPeakHoursActive(false);
        clearClassificationFilters();
        return;
      }
      const hasOnlyDateTimeFilters = filterState.filter.every(
        (filter) => filter.type === FilterType.DATE || filter.type === FilterType.TIME
      );
      setFilterState((prevState) => ({ ...prevState, filter: [] }));
      setAutoSelectCalendar(true);
      setTimeWindows(null);
      if (hasOnlyDateTimeFilters) {
        setAppMode(dashboard ? AppMode.TWIN_SELECTION : AppMode.LIVE);
        setStartDateTime(null);
        setFinishDateTime(null);
      } else {
        setDefaultTimeRange();
      }
    },
    [
      setFilterState,
      setTimeWindows,
      setWeekdayWindows,
      setPeakHoursActive,
      setAutoSelectCalendar,
      setDefaultTimeRange,
      setAppMode,
      setStartDateTime,
      setFinishDateTime,
      filterState.filter,
      appMode,
      clearClassificationFilters
    ]
  );
  const toggleEquipmentSelection = useCallback3(
    (equipment) => {
      const existingIndex = filterState.filter.findIndex(
        (item) => item.type === FilterType.ASSET && item.id === equipment.id
      );
      if (existingIndex >= 0) {
        removeFilter(existingIndex);
      } else {
        addFilter({
          type: FilterType.ASSET,
          id: equipment.id,
          bId: equipment.bId,
          label: equipment.label,
          display: true
        });
      }
    },
    [filterState.filter, removeFilter, addFilter]
  );
  useEffect2(() => {
    if (!filterState.autoSelectCalendar && filterState.timeWindows && filterState.timeWindows.length > 0) {
      const allDates = [];
      filterState.timeWindows.forEach(([start, end]) => {
        allDates.push(start, end);
      });
      const { minDate, maxDate } = findMinMaxDates(allDates);
      if (minDate && maxDate) {
        let normalizedMaxDate = maxDate;
        const maxDateTime = DateTime.fromISO(maxDate);
        if (maxDateTime.hour === 0 && maxDateTime.minute === 0 && maxDateTime.second === 0) {
          const normalizedISO = maxDateTime.minus({ days: 1 }).endOf("day").toISO({ includeOffset: false });
          if (normalizedISO) {
            normalizedMaxDate = normalizedISO;
          }
        }
        setStartDateTime(minDate);
        setFinishDateTime(normalizedMaxDate);
      }
    }
  }, [
    filterState.timeWindows,
    filterState.autoSelectCalendar,
    setStartDateTime,
    setFinishDateTime
  ]);
  useEffect2(() => {
    if (filterState.startDateTime && filterState.finishDateTime) {
      removeFilterByType(FilterType.DATE);
      removeFilterByType(FilterType.TIME);
      const pills = getSelectionProfile(AppMode.HISTORICAL).derivePills(
        filterState.startDateTime,
        filterState.finishDateTime
      );
      setFilterState((prevState) => ({
        ...prevState,
        filter: [pills.date, pills.time, ...prevState.filter]
      }));
    }
  }, [filterState.startDateTime, filterState.finishDateTime, removeFilterByType]);
  useEffect2(() => {
    if (!isDatelessCalendarMode(appMode)) {
      return;
    }
    removeFilterByType(FilterType.DATE);
    removeFilterByType(FilterType.TIME);
    removeFilterByType(FilterType.PEAK);
    if (!filterState.weekdayWindows || filterState.weekdayWindows.length === 0) {
      return;
    }
    if (filterState.peakHoursActive) {
      const peakPill = { type: FilterType.PEAK, label: "Peak hours", display: true };
      setFilterState((prevState) => ({
        ...prevState,
        filter: [peakPill, ...prevState.filter]
      }));
      return;
    }
    const pills = getSelectionProfile(appMode)?.deriveWeekdayPills?.(filterState.weekdayWindows);
    if (pills) {
      setFilterState((prevState) => ({
        ...prevState,
        filter: [pills.date, pills.time, ...prevState.filter]
      }));
    }
  }, [filterState.weekdayWindows, filterState.peakHoursActive, appMode, removeFilterByType]);
  useEffect2(() => {
    removeFilterByType(FilterType.EQUIPMENT_TYPE);
    removeFilterByType(FilterType.MANUFACTURER);
    removeFilterByType(FilterType.FLOOR);
    if (appMode !== AppMode.EQUIPMENT) {
      return;
    }
    const { equipmentType, manufacturer, floor } = filterState.classificationFilters;
    if (equipmentType.length === 0 && manufacturer.length === 0 && floor.length === 0) {
      return;
    }
    const pills = [
      ...equipmentType.map((node) => ({
        type: FilterType.EQUIPMENT_TYPE,
        bId: node.bid,
        label: node.label,
        display: true
      })),
      ...manufacturer.map((node) => ({
        type: FilterType.MANUFACTURER,
        bId: node.bid,
        label: node.label,
        display: true
      })),
      ...floor.map((node) => ({
        type: FilterType.FLOOR,
        bId: node.bid,
        label: node.label,
        display: true
      }))
    ];
    setFilterState((prevState) => ({
      ...prevState,
      filter: [...prevState.filter, ...pills]
    }));
  }, [filterState.classificationFilters, appMode, removeFilterByType]);
  useEffect2(() => {
    clearClassificationFilters();
  }, [twinBId, clearClassificationFilters]);
  const filterContextValue = useMemo2(
    () => ({
      ...filterState,
      setLastUpdatedAt,
      setHeroMetric,
      setFilter,
      addFilter,
      removeFilter,
      removeFilterByType,
      removeFilterById,
      clearFilter,
      toggleEquipmentSelection,
      setStartDateTime,
      setFinishDateTime,
      setTimeWindows,
      setAutoSelectCalendar,
      setHistoricalReset,
      commitSelection,
      resetSelectionToDefault,
      setWeekdayWindows,
      commitWeekdaySelection,
      resetWeekdaySelection,
      setPeakHoursActive,
      toggleClassificationFilter,
      setClassificationFilter,
      clearClassificationFilters
    }),
    [
      filterState,
      setLastUpdatedAt,
      setHeroMetric,
      setFilter,
      addFilter,
      removeFilter,
      removeFilterByType,
      removeFilterById,
      clearFilter,
      toggleEquipmentSelection,
      setStartDateTime,
      setFinishDateTime,
      setTimeWindows,
      setAutoSelectCalendar,
      setHistoricalReset,
      commitSelection,
      resetSelectionToDefault,
      setWeekdayWindows,
      commitWeekdaySelection,
      resetWeekdaySelection,
      setPeakHoursActive,
      toggleClassificationFilter,
      setClassificationFilter,
      clearClassificationFilters
    ]
  );
  const modeContextValue = useMemo2(() => {
    return {
      appMode,
      calendarView,
      setAppModeWithFilterReset,
      setCurrentCalendarView
    };
  }, [appMode, calendarView, setAppModeWithFilterReset, setCurrentCalendarView]);
  return /* @__PURE__ */ jsx3(FilterContext.Provider, { value: filterContextValue, children: /* @__PURE__ */ jsx3(ModeContext.Provider, { value: modeContextValue, children: props.children }) });
};

// src/contexts/EventContext.tsx
import {
  AppMode as AppMode2,
  FilterType as FilterType2
} from "@repo/frontend-types";
import {
  createContext as createContext4,
  useCallback as useCallback4,
  useContext as useContext4,
  useEffect as useEffect3,
  useMemo as useMemo3,
  useReducer,
  useRef as useRef2
} from "react";
import {
  buildEquipmentClassificationTrees,
  buildFloorFilterTree,
  collectEquipmentEntities,
  dataModeFor,
  entityKeyFor,
  findEntityById
} from "@repo/front-end-utils";
import { jsx as jsx4 } from "react/jsx-runtime";
var initialState5 = {
  currentHoveredEntity: null,
  currentHoveredEntityOn3DScene: null,
  selectedEntityIds: /* @__PURE__ */ new Set(),
  selectedEquipmentBids: null,
  setCurrentHoveredValue: () => {
  },
  setCurrentHoveredEntityOn3DScene: () => {
  }
};
var reducer = (state, action) => {
  switch (action.type) {
    case 0 /* setCurrentHoveredValue */:
      return {
        ...state,
        currentHoveredEntity: action.payload
      };
    case 1 /* setCurrentHoveredEntityOn3DScene */:
      return {
        ...state,
        currentHoveredEntityOn3DScene: action.payload
      };
    case 2 /* setSelectedEntityIds */:
      return {
        ...state,
        selectedEntityIds: action.payload
      };
    case 3 /* reset */:
      return { ...state, ...initialState5, selectedEntityIds: /* @__PURE__ */ new Set() };
    default:
      return state;
  }
};
var EventContext = createContext4(initialState5);
var useEventContext = () => {
  return useContext4(EventContext);
};
var flattenTrees = (trees) => {
  const map = /* @__PURE__ */ new Map();
  const walk = (nodes) => {
    for (const node of nodes) {
      map.set(node.bid, node.equipmentBids);
      walk(node.children);
    }
  };
  walk(trees.equipmentType);
  walk(trees.manufacturer);
  return map;
};
var deriveEquipmentSelection = (filter, allEquipment, classificationFilters = {
  equipmentType: [],
  manufacturer: [],
  floor: []
}, trees = { equipmentType: [], manufacturer: [] }, floorTree = []) => {
  const assetFilters = filter.filter((filterItem) => filterItem.type === FilterType2.ASSET);
  const explicitIds = assetFilters.length > 0 ? new Set(assetFilters.map((f) => f.id).filter((id) => Boolean(id))) : new Set(allEquipment.map((equipment) => equipment.id));
  if (classificationFilters.equipmentType.length === 0 && classificationFilters.manufacturer.length === 0 && classificationFilters.floor.length === 0) {
    return explicitIds;
  }
  const bidToEquipmentBids = flattenTrees(trees);
  for (const floorNode of floorTree) {
    bidToEquipmentBids.set(floorNode.bid, floorNode.equipmentBids);
  }
  const perHierarchyMatchSets = [];
  for (const nodes of [
    classificationFilters.equipmentType,
    classificationFilters.manufacturer,
    classificationFilters.floor
  ]) {
    if (nodes.length === 0) {
      continue;
    }
    const union = /* @__PURE__ */ new Set();
    for (const node of nodes) {
      for (const bid of bidToEquipmentBids.get(node.bid) ?? []) {
        union.add(bid);
      }
    }
    perHierarchyMatchSets.push(union);
  }
  if (perHierarchyMatchSets.length === 0) {
    return explicitIds;
  }
  const [first, ...rest] = perHierarchyMatchSets;
  const classMatchBids = new Set(Array.from(first).filter((bid) => rest.every((set) => set.has(bid))));
  const result = /* @__PURE__ */ new Set();
  for (const equipment of allEquipment) {
    if (classMatchBids.has(equipment.bID) && explicitIds.has(equipment.id)) {
      result.add(equipment.id);
    }
  }
  return result;
};
var deriveEquipmentSelectionBids = (filter, allEquipment, classificationFilters = {
  equipmentType: [],
  manufacturer: [],
  floor: []
}, trees = { equipmentType: [], manufacturer: [] }, floorTree = []) => {
  const selectedIds = deriveEquipmentSelection(
    filter,
    allEquipment,
    classificationFilters,
    trees,
    floorTree
  );
  const bids = /* @__PURE__ */ new Set();
  for (const equipment of allEquipment) {
    if (selectedIds.has(equipment.id) && equipment.bID) {
      bids.add(equipment.bID);
    }
  }
  return Array.from(bids).sort();
};
var collectEntityAndChildrenIds = (entity, selectedIds) => {
  selectedIds.add(entity.id);
  if (entity.children && entity.children.length > 0) {
    entity.children.forEach((child) => collectEntityAndChildrenIds(child, selectedIds));
  }
};
var EventContextProvider = (props) => {
  const [state, dispatch] = useReducer(reducer, initialState5);
  const { filter, classificationFilters } = useFilterContext();
  const { appMode } = useModeContext();
  const { twin, multifloor } = useTwinContext();
  const { classifications } = useEquipmentClassificationStore();
  const equipmentEntities = useMemo3(
    () => twin?.physicalModel ? collectEquipmentEntities(twin.physicalModel) : [],
    [twin]
  );
  const classificationTrees = useMemo3(
    // `?? []` for parity with EquipmentFilterTab — tolerate a malformed (nullish) store value
    // rather than throwing inside the builder.
    () => buildEquipmentClassificationTrees(classifications ?? []),
    [classifications]
  );
  const floorTree = useMemo3(
    () => buildFloorFilterTree(twin?.physicalModel, multifloor),
    [twin, multifloor]
  );
  const setCurrentHoveredValue = useCallback4(
    (currentHoveredEntity) => {
      dispatch({
        type: 0 /* setCurrentHoveredValue */,
        payload: currentHoveredEntity
      });
    },
    []
  );
  const setCurrentHoveredEntityOn3DScene = useCallback4(
    (currentHoveredEntityOn3DScene) => {
      dispatch({
        type: 1 /* setCurrentHoveredEntityOn3DScene */,
        payload: currentHoveredEntityOn3DScene
      });
    },
    []
  );
  const resetKeyRef = useRef2(props.resetKey);
  useEffect3(() => {
    if (resetKeyRef.current === props.resetKey) return;
    resetKeyRef.current = props.resetKey;
    dispatch({ type: 3 /* reset */ });
  }, [props.resetKey]);
  useEffect3(() => {
    const selectedIds = /* @__PURE__ */ new Set();
    if (twin) {
      if (appMode === AppMode2.EQUIPMENT) {
        dispatch({
          type: 2 /* setSelectedEntityIds */,
          payload: deriveEquipmentSelection(
            filter,
            equipmentEntities,
            classificationFilters,
            classificationTrees,
            floorTree
          )
        });
        return;
      }
      let lastFilterId = twin?.physicalModel[entityKeyFor(appMode)];
      if (filter.length > 0) {
        let filterEntities = filter.filter((filterItem) => filterItem.type === FilterType2.ENTITY);
        const lastFilterItem = filterEntities.pop();
        filterEntities.forEach((filterItem) => {
          filterItem.id && selectedIds.add(filterItem.id);
        });
        if (lastFilterItem) {
          if (entityKeyFor(appMode) === "id" && lastFilterItem.id) {
            lastFilterId = lastFilterItem.id;
          } else if (entityKeyFor(appMode) === "bID" && lastFilterItem.bId) {
            lastFilterId = lastFilterItem.bId;
          }
        }
      }
      const entities = [twin?.physicalModel];
      const matchedEntity = findEntityById(dataModeFor(appMode), entities, lastFilterId);
      if (matchedEntity) {
        collectEntityAndChildrenIds(matchedEntity, selectedIds);
      }
      dispatch({
        type: 2 /* setSelectedEntityIds */,
        payload: selectedIds
      });
    }
  }, [
    filter,
    appMode,
    twin,
    equipmentEntities,
    classificationFilters,
    classificationTrees,
    floorTree,
    props.resetKey
  ]);
  const rawEquipmentBids = useMemo3(
    () => appMode === AppMode2.EQUIPMENT && twin ? deriveEquipmentSelectionBids(
      filter,
      equipmentEntities,
      classificationFilters,
      classificationTrees,
      floorTree
    ) : null,
    [
      appMode,
      twin,
      filter,
      equipmentEntities,
      classificationFilters,
      classificationTrees,
      floorTree
    ]
  );
  const equipmentBidsKey = rawEquipmentBids === null ? null : rawEquipmentBids.join("\0");
  const selectedEquipmentBids = useMemo3(
    () => rawEquipmentBids,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on membership, not array identity
    [equipmentBidsKey]
  );
  const contextValue = useMemo3(() => {
    return {
      ...state,
      selectedEquipmentBids,
      setCurrentHoveredValue,
      setCurrentHoveredEntityOn3DScene
    };
  }, [state, selectedEquipmentBids, setCurrentHoveredValue, setCurrentHoveredEntityOn3DScene]);
  return /* @__PURE__ */ jsx4(EventContext.Provider, { value: contextValue, children: props.children });
};

// src/contexts/DataContext.tsx
import { createContext as createContext5, useContext as useContext5 } from "react";
var initialDataContextState = {
  data: { raw: [], processed: null },
  liveData: null,
  warning: false
};
var DataContext = createContext5(initialDataContextState);
var useDataContext = () => {
  return useContext5(DataContext);
};

// src/hooks/useAddMissingRegisterRows.ts
import { useCallback as useCallback5, useEffect as useEffect4, useRef as useRef3, useState as useState4 } from "react";
var NAMED_FAILURE_LIMIT = 5;
var useAddMissingRegisterRows = ({
  twinId,
  twinBid,
  registerRead,
  nextStepHint
}) => {
  const createRegisterAsset = useEquipmentCatalogStore((s) => s.createRegisterAsset);
  const mounted = useRef3(true);
  useEffect4(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const [addingProgress, setAddingProgress] = useState4(null);
  const [error, setError] = useState4(null);
  const [notice, setNotice] = useState4(null);
  const clearMessages = useCallback5(() => {
    setError(null);
    setNotice(null);
  }, []);
  const addMissing = useCallback5(
    async (entities) => {
      setError(null);
      setNotice(null);
      if (entities.length === 0) return;
      if (!twinId || !twinBid) {
        console.error("[register-drift] refused \u2014 no twin context", { twinId, twinBid });
        setError("Missing twin context, so nothing was added to the register. Reload the page.");
        return;
      }
      if (registerRead !== "ready") {
        console.error("[register-drift] refused \u2014 register not read", { registerRead });
        setError(
          "The register has not been read yet, so we cannot tell what is missing from it. Nothing was added \u2014 wait for it to load, or reload the page."
        );
        return;
      }
      setAddingProgress({ done: 0, total: entities.length });
      let created = 0;
      let abandoned = 0;
      const failures = [];
      try {
        for (const entity of entities) {
          const contextTwinId = useEquipmentCatalogStore.getState().registerContext?.twinId ?? null;
          if (contextTwinId !== twinId) {
            abandoned = entities.length - created - failures.length;
            console.error("[register-drift] stopped \u2014 the register scope changed mid-run", {
              expected: twinId,
              found: contextTwinId,
              created,
              abandoned
            });
            break;
          }
          if (!mounted.current) {
            abandoned = entities.length - created - failures.length;
            break;
          }
          try {
            await createRegisterAsset({
              name: entity.name,
              entityCode: entity.identity,
              modelFile: entity.modelFile
            });
            created += 1;
          } catch (err) {
            console.error("[register-drift] create failed", {
              identity: entity.identity,
              name: entity.name,
              error: err
            });
            failures.push(
              `${entity.name} (${entity.identity}): ${err instanceof Error ? err.message : "the asset could not be added"}`
            );
          }
          setAddingProgress({ done: created + failures.length, total: entities.length });
        }
      } finally {
        setAddingProgress(null);
      }
      if (!mounted.current) return;
      if (abandoned > 0) {
        setError(
          `Added ${created} of ${entities.length} to the register, then stopped: the register being shown changed while it was running. The ${created} already created are saved. Press again to add the remaining ${abandoned}.`
        );
        return;
      }
      if (failures.length === 0) {
        setNotice(
          `Added ${created} ${created === 1 ? "asset" : "assets"} to the register. ` + nextStepHint
        );
        return;
      }
      const named = failures.slice(0, NAMED_FAILURE_LIMIT).join("; ");
      const unnamed = failures.length - Math.min(failures.length, NAMED_FAILURE_LIMIT);
      setError(
        `Added ${created} of ${entities.length} to the register. ${failures.length} could not be added: ${named}` + (unnamed > 0 ? `; and ${unnamed} more \u2014 the browser console lists every one.` : "")
      );
    },
    [twinId, twinBid, registerRead, nextStepHint, createRegisterAsset]
  );
  return { addMissing, addingProgress, error, notice, clearMessages };
};
export {
  ConfigurationSource,
  DEFAULT_DIRECTIONAL_LIGHT_PROPS,
  DEFAULT_HEMI_LIGHT_PROPS,
  DataContext,
  DialogContext,
  DialogContextProvider,
  EDITOR_MAX_ZOOM,
  EDITOR_MIN_ZOOM,
  EventContext,
  EventContextProvider,
  FilterAndModeContextsProvider,
  FilterContext,
  NO_PRODUCT_LINE,
  NotificationContextProvider,
  PLAN_MODE_DESCRIPTION,
  REGISTER_READ_FAILED_MESSAGE,
  RegisterWriteRefused,
  createRequestSequencer,
  deriveEquipmentSelection,
  extractTwinMarkerData,
  flyToOnPlan,
  getDefaultViewerParams,
  groupSiteAssets,
  initMariaConfigFromEnv,
  initUiSettingsFromEnv,
  initUserStoreFromStorage,
  initialDataContextState,
  isFloorMove,
  layoutWithDraftBasis,
  planSurfaceRemoval,
  projectRegisterRows,
  registerReadState,
  reparentFromDrawing,
  resolveRegisterScope,
  runLatestWins,
  selectEntityOnFloor,
  selectionOnFloorShown,
  siteAssetGroupKeys,
  useAddMissingRegisterRows,
  useAdminStore,
  useCameraCatalogStore,
  useCameraHistory,
  useCameraSource,
  useCameraSummary,
  useCurrentCamera,
  useCurrentMapOrigin,
  useDataContext,
  useDeveloperEnabled,
  useDeveloperStore,
  useDialogContext,
  useEditorStore,
  useEquipmentCatalogStore,
  useEquipmentClassificationStore,
  useEventContext,
  useFilterContext,
  useFloorplanStore,
  useLegalNotices,
  useManufacturerCatalogStore,
  useMapOriginHistory,
  useMapOriginSource,
  useMapOriginSummary,
  useMariaConfigStore,
  useModeContext,
  useNotificationContext,
  useNotificationError,
  useNotificationIsLoading,
  useNotificationLoadingDetail,
  useNotificationStore,
  useNotifications,
  useOrganizationStore,
  useScaleCalibrationStore,
  useSelectedNotification,
  useSelectedTwinBID,
  useSettingsStore,
  useSpotlightStore,
  useTimelineCalendarSyncStore,
  useTwinConfigStore,
  useTwinContext,
  useTwinCount,
  useTwinLoading,
  useTwinMarkers,
  useTwinModelStore,
  useTwinStore,
  useUiSettingsStore,
  useUnreadCount,
  useUserStore
};
